import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './GrantQuestionnaire.css';
import EmailPreviewEditModal from '../components/EmailPreviewEditModal';
import { PROJECT_ROLES } from '../utils/keyPersonnel';
import {
  BUSINESS_TYPE_OPTIONS,
  DEFAULT_BUSINESS_TYPE,
  SMALL_BUSINESS_TYPE,
  OTHER_BUSINESS_TYPE,
  CONGRESSIONAL_DISTRICT_URL
} from '../utils/businessTypes';

// Show a money amount with thousands separators while keeping what the user is
// typing (a trailing "." or partial decimals) intact.
const formatMoneyInput = (raw) => {
  const str = String(raw ?? '');
  if (!str) return '';
  const [intPart, decPart] = str.split('.');
  const withCommas = (intPart || '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
};
const parseMoneyInput = (display) => {
  const cleaned = String(display || '').replace(/[^0-9.]/g, '');
  const [intPart, ...rest] = cleaned.split('.');
  return rest.length ? `${intPart}.${rest.join('').slice(0, 2)}` : intPart;
};

// Sidebar links, in the same order the sections appear on the page.
const TOC_SECTIONS = [
  { id: 'basic-information', label: '1. Basic Information' },
  { id: 'rr-cover', label: '2. R&R Cover' },
  { id: 'cover-page-supplement', label: '3. Cover Page Supplement' },
  { id: 'additional-questions', label: '4. Other Project Information' },
  { id: 'performance-site', label: '5. Project Performance Sites' },
  { id: 'key-personnel', label: '6. Senior/Key Personnel' },
  { id: 'sbir-sttr-program', label: '7. SBIR/STTR Information' },
  { id: 'human-subjects-clinical-trials', label: '8. Human Subjects and Clinical Trials' }
];

// R&R Cover, "Type of Submission"
const SUBMISSION_TYPES = ['Pre-Application', 'Application', 'Changed/Corrected Application'];

// Type of Application, used by both the drop-down and the Grant Submission portal
const GRANT_APPLICATION_TYPES = ['New', 'Resubmission', 'Renewal', 'Continuation', 'Revision'];

// Boxes marked when the application is a Revision
const REVISION_OPTIONS = [
  { field: 'revisionIncreaseAward', label: 'A. Increase Award' },
  { field: 'revisionDecreaseAward', label: 'B. Decrease Award' },
  { field: 'revisionIncreaseDuration', label: 'C. Increase Duration' },
  { field: 'revisionDecreaseDuration', label: 'D. Decrease Duration' },
  { field: 'revisionOther', label: 'E. Other (specify)' }
];


// The state-review list the E.O. 12372 question links to
const SPOC_LIST_URL =
  'https://bidenwhitehouse.archives.gov/wp-content/uploads/2024/08/SPOC-list-as-of-August-2024.pdf';

// One senior/key person. Address details are filled from the company record when
// the client company is the selected organization.
const EMPTY_KEY_PERSON = {
  eraCommonsUsername: '',
  firstName: '',
  lastName: '',
  // Position/Title is shared with the biosketch on the Upload Documents page
  positionTitle: '',
  projectRole: '',
  projectRoleOther: '',
  organizationName: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zipCode: '',
  country: 'UNITED STATES',
  phoneNumber: '',
  email: ''
};

// The applicant details the R&R Cover shows read-only; editing one of these here
// also updates the Company Information page.
const APPLICANT_FIELDS = [
  { field: 'ueiNumber', label: 'UEI' },
  { field: 'companyName', label: 'Legal Business Name' },
  { field: 'businessAddressLine1', label: 'Street' },
  { field: 'businessAddressCity', label: 'City' },
  { field: 'businessAddressZip', label: 'Zip/Postal Code' },
  { field: 'signingOfficialFirstName', label: 'Contact First Name' },
  { field: 'signingOfficialLastName', label: 'Contact Last Name' },
  { field: 'signingOfficialPhone', label: 'Contact Phone Number' },
  { field: 'signingOfficialEmail', label: 'Contact Email' },
  { field: 'einNumber', label: 'EIN' },
  { field: 'businessType', label: 'Type of Applicant' }
];

const GrantQuestionnaire = ({ userInfo }) => {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  // Determine if user is an admin
  const isAdmin = userInfo?.accountType === 'admin' ||
                  userInfo?.accountType === 'superadmin' ||
                  userInfo?.role === 'admin' ||
                  userInfo?.role === 'superadmin';
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Lock-state tracking: when isComplete is true, the form is read-only until unlocked.
  const [questionnaireMeta, setQuestionnaireMeta] = useState({ isComplete: false, completedAt: null });
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const performUnlock = async () => {
    setUnlocking(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/questionnaire/unlock`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (r.ok) {
        setQuestionnaireMeta({ isComplete: false, completedAt: null });
        setShowUnlockModal(false);
      } else {
        alert('Failed to unlock questionnaire.');
      }
    } catch (e) {
      console.error('Error unlocking questionnaire:', e);
      alert('Failed to unlock questionnaire.');
    } finally {
      setUnlocking(false);
    }
  };
  const [error, setError] = useState(null);
  const [application, setApplication] = useState(null);
  const [savedSites, setSavedSites] = useState([]);
  const [showSiteAutofill, setShowSiteAutofill] = useState({});

  // Subcontract email preview states
  const [showSubcontractEmailPreview, setShowSubcontractEmailPreview] = useState(false);
  const [subcontractEmailVariables, setSubcontractEmailVariables] = useState({});
  const [subcontractRecipientEmail, setSubcontractRecipientEmail] = useState('');
  const [subcontractCcEmails, setSubcontractCcEmails] = useState([]);
  const [currentSubcontractSiteIndex, setCurrentSubcontractSiteIndex] = useState(null);
  const [sendingSubcontractEmails, setSendingSubcontractEmails] = useState(false);
  const [sentSubcontractRequests, setSentSubcontractRequests] = useState(new Set());

  // Questionnaire form state
  const [formData, setFormData] = useState({
    // R&R Cover — type of submission
    typeOfSubmission: 'Application',

    // Grant Application Type
    grantApplicationType: '',
    priorGrantNumber: '',
    // Revision applications mark one or more of these boxes
    revisionIncreaseAward: false,
    revisionDecreaseAward: false,
    revisionIncreaseDuration: false,
    revisionDecreaseDuration: false,
    revisionOther: false,
    revisionOtherSpecify: '',
    // Submitted elsewhere?
    submittedToOtherAgencies: 'No',
    otherAgenciesNames: '',
    // Congressional district of the primary performance site (kept in step with
    // the Company Information page)
    congressionalDistrict: '',

    // Estimated project funding
    totalFederalFunds: '',
    totalNonFederalFunds: '',
    estimatedProgramIncome: '',
    // E.O. 12372 state review
    eo12372Status: '',
    eo12372ReviewDate: '',

    // Senior/Key Personnel (section 6). The first entry is the PI.
    keyPersonnel: [],
    
    // Sub-contracts
    hasSubcontracts: 'No',
    numberOfSubcontracts: 0,
    subcontracts: [],
    
    rfaNumber: '',
    proposalTitle: '',
    piFirstName: '',
    piMiddleName: '',
    piLastName: '',
    piPhoneNumber: '',
    startDate: '',
    endDate: '',
    projectSites: [{
      organizationName: '',
      ueiNumber: '',
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
      congressionalDistrict: '',
      isPrimary: true,
      hasSubcontractedWork: 'No',
      willReceiveSubcontract: '', // Yes/No for additional sites only (not primary)
      subcontractContactEmail: '', // Legacy: single contact email (kept for older saved drafts)
      subcontractPIName: '', // Name of the sub-contract PI
      subcontractPIEmail: '', // Email of the sub-contract PI (e.g. university faculty member)
      subcontractBusinessOfficeEmail: '' // Email of the sub-contract PI's business office (optional)
    }],
    additionalLocations: '',
    
    // Vertebrate Animals Section
    vertebrateAnimalsInvolved: 'No',
    vertebrateAnimalsUsed: 'No', // Second vertebrate animals question
    animalsEuthanized: 'No',
    avmaGuidelines: 'Yes',
    nonAvmaJustification: '',
    
    // Program Income Section
    programIncomeAnticipated: 'No',
    programIncomeDetails: '',
    // One row per budget period when program income is anticipated
    programIncomePeriods: [],
    
    // Human Embryonic Stem Cells Section
    humanEmbryonicStemCells: 'No',
    registryCellLineToBeUsed: false,
    cellLines: '',
    
    // Human Fetal Tissue Section
    humanFetalTissue: 'No',
    hftComplianceAssurance: '',
    hftSampleIrbConsent: '',

    hasInventionsAndPatents: 'No',
    previouslyReported: 'No',
    
    // 6. Change of Investigator/Change of Organization
    changeInInvestigatorOrOrg: 'No',
    formerPIPrefix: '',
    formerPIFirstName: '',
    formerPIMiddleName: '',
    formerPILastName: '',
    formerPISuffix: '',
    formerOrganizationName: '',
    
    // Additional Required Questions
    // 1. Human Subjects
    humanSubjectsInvolved: 'No',
    isExemptFromFederalRegulations: 'No',
    exemptionNumber: '',
    isIRBPending: 'No',
    irbApprovalDate: '',
    humanSubjectsAssuranceNumber: '',
    humanSpecimensExplanation: '',
    
    // 2. Vertebrate Animals Additional Info
    iacucReviewPending: 'No',
    iacucApprovalDate: '',
    assuranceNumber: '',
    
    // 3. Proprietary Information
    containsProprietaryInfo: 'No',
    
    // 4. Environmental Impact
    hasEnvironmentalImpact: 'No',
    environmentalImpactExplanation: '',
    environmentalAssessmentPerformed: 'No',
    environmentalAssessmentExplanation: '',
    
    // 5. Historic Place
    isHistoricPlace: 'No',
    historicPlaceExplanation: '',
    
    // 6. International Activities
    involvesInternationalActivities: 'No',
    internationalCountries: '',
    internationalExplanation: '',

    agency: '',
    agencyOther: '',
    programType: '',
    applicationType: '',
    phaseILetterOfIntentNumber: '',
    agencyTopicSubtopic: '',
    
    // SBIR/STTR Questions 1-8
    meetEligibilityCriteria: 'Yes',
    anticipatedPersonnelCount: '',
    majorityOwnedByVC: 'No',
    facultyOrStudentOwned: 'No',
    includesSubcontractsWithFedLabs: 'No',
    fedLabsAgencies: '',
    locatedInHUBZone: 'No',
    researchInUS: 'Yes',
    researchNotInUSExplanation: '',
    submittedEquivalentWork: 'No',
    equivalentWorkAgencies: '',
    allowInfoDisclosure: 'Yes',
    requestingTABAFunds: 'No',
    tabaFundsBudget: '',
    tabaFundsJustification: '',

    receivedSBIRPhaseII: 'No',
    piPrimaryEmploymentWithSmallBusiness: 'Yes',
    
    // STTR-Specific Questions (11-13)
    piCommitmentAndEffort: 'Yes',
    workDistributionRequirementMet: 'Yes',
    nonprofitResearchPartnerUEI: '',
    
    // Human specimens/data question
    involvesHumanSpecimensOrData: 'No',
  });

  const handleProjectSiteChange = (index, field, value) => {
    setFormData(prevData => {
      const updatedSites = [...prevData.projectSites];
      updatedSites[index] = {
        ...updatedSites[index],
        [field]: value
      };
      return {
        ...prevData,
        projectSites: updatedSites
      };
    });
  };

  const addProjectSite = () => {
    setFormData(prevData => ({
      ...prevData,
      projectSites: [
        ...prevData.projectSites,
        {
          organizationName: '',
          ueiNumber: '',
          street1: '',
          street2: '',
          city: '',
          state: '',
          zipCode: '',
          congressionalDistrict: '',
          isPrimary: false,
          hasSubcontractedWork: 'No',
          willReceiveSubcontract: '',
          subcontractContactEmail: '',
          subcontractPIName: '',
          subcontractPIEmail: '',
          subcontractBusinessOfficeEmail: ''
        }
      ]
    }));
  };

  const removeProjectSite = (index) => {
    if (index === 0 && formData.projectSites.length === 1) {
      // Don't remove the last site, but clear it
      handleProjectSiteChange(0, 'organizationName', '');
      handleProjectSiteChange(0, 'ueiNumber', '');
      handleProjectSiteChange(0, 'street1', '');
      handleProjectSiteChange(0, 'street2', '');
      handleProjectSiteChange(0, 'city', '');
      handleProjectSiteChange(0, 'state', '');
      handleProjectSiteChange(0, 'zipCode', '');
      handleProjectSiteChange(0, 'congressionalDistrict', '');
      return;
    }
    
    setFormData(prevData => {
      const updatedSites = [...prevData.projectSites];
      updatedSites.splice(index, 1);
      
      // Ensure the first site is marked as primary
      if (updatedSites.length > 0 && !updatedSites.some(site => site.isPrimary)) {
        updatedSites[0].isPrimary = true;
      }
      
      return {
        ...prevData,
        projectSites: updatedSites
      };
    });
  };

  // Fetch saved performance sites
  const fetchSavedSites = async () => {
    try {
      console.log('Fetching saved sites from:', `${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/saved-sites`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/saved-sites`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('Saved sites response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Saved sites data:', data);
        const sites = data.sites || [];
        console.log('Setting saved sites:', sites);
        setSavedSites(sites);
        
        // No mock data - showing actual backend data only
        console.log('Backend returned', sites.length, 'saved sites');
      } else {
        console.error('Failed to fetch saved sites, status:', response.status);
        const errorText = await response.text();
        console.error('Error response:', errorText);
        
        // No mock data fallback - showing actual backend response
        console.log('Backend returned error, setting empty array');
        setSavedSites([]);
      }
    } catch (error) {
      console.error('Error fetching saved sites:', error);
      // No mock data in catch block - showing actual error state
      console.log('Fetch failed, setting empty array');
      setSavedSites([]);
    }
  };

  // Auto-fill site with saved data
  const handleSiteAutofill = (index, savedSite) => {
    const siteData = {
      organizationName: savedSite.organizationName,
      ueiNumber: savedSite.ueiNumber,
      street1: savedSite.street1,
      street2: savedSite.street2 || '',
      city: savedSite.city,
      state: savedSite.state,
      zipCode: savedSite.zipCode,
      congressionalDistrict: savedSite.congressionalDistrict
    };

    Object.keys(siteData).forEach(field => {
      handleProjectSiteChange(index, field, siteData[field]);
    });

    setShowSiteAutofill(prevState => ({ ...prevState, [index]: false }));
  };

  // Toggle autofill dropdown for a specific site
  const toggleSiteAutofill = (index) => {
    setShowSiteAutofill(prevState => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };

  // Cancel autofill for a specific site
  const cancelSiteAutofill = (index) => {
    setShowSiteAutofill(prevState => ({ ...prevState, [index]: false }));
  };

  // Note: Performance sites are now automatically available for autofill
  // from existing questionnaire submissions - no separate save needed

  // Sub-contract handlers
  const handleSubcontractChange = (e) => {
    const value = e.target.value;
    setFormData(prevData => ({
      ...prevData,
      hasSubcontracts: value,
      numberOfSubcontracts: value === 'No' ? 0 : prevData.numberOfSubcontracts,
      subcontracts: value === 'No' ? [] : prevData.subcontracts
    }));
  };

  const handleNumberOfSubcontractsChange = (e) => {
    const count = parseInt(e.target.value) || 0;
    setFormData(prevData => {
      // Initialize subcontracts array with empty objects
      const newSubcontracts = Array.from({ length: count }, (_, index) => 
        prevData.subcontracts[index] || {
          organizationName: '',
          ueiNumber: '',
          street1: '',
          street2: '',
          city: '',
          state: '',
          county: '',
          zipCode: '',
          congressionalDistrict: ''
        }
      );
      
      return {
        ...prevData,
        numberOfSubcontracts: count,
        subcontracts: newSubcontracts
      };
    });
  };

  const handleSubcontractSiteChange = (index, field, value) => {
    setFormData(prevData => {
      const updatedSubcontracts = [...prevData.subcontracts];
      updatedSubcontracts[index] = {
        ...updatedSubcontracts[index],
        [field]: value
      };
      return {
        ...prevData,
        subcontracts: updatedSubcontracts
      };
    });
  };
  

  // Fetch existing questionnaire data if available
  useEffect(() => {
    const fetchApplicationData = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });


        if (!response.ok) {
          throw new Error('Failed to fetch application data');
        }

        const data = await response.json();

        setApplication(data.application);

        console.log("application data: ", data.application);

        // Pre-fill basic fields from application data
        const applicationData = {};

        // Pre-fill RFA number and proposal title from application data
        if (data.application.paRfaNumber) {
          applicationData.rfaNumber = data.application.paRfaNumber;
        }
        if (data.application.name) {
          applicationData.proposalTitle = data.application.name;
        }

        // If questionnaire data exists, populate the form
        if (data.application.questionnaire) {
          // Capture lock metadata so the form can render a "Completed" banner and disable inputs.
          setQuestionnaireMeta({
            isComplete: data.application.questionnaire.isComplete === true ||
                        data.application.questionnaire.isComplete === 'true',
            completedAt: data.application.questionnaire.completedAt || null
          });
          // Handle potential migration from old format (single piName) to new format
          const questionnaireData = { ...data.application.questionnaire.answers };
          
          // If there's a single piName field but no separate name fields,
          // try to split it into first, middle, last
          if (questionnaireData.piName && 
              !questionnaireData.piFirstName && 
              !questionnaireData.piLastName) {
            
            const nameParts = questionnaireData.piName.trim().split(' ');
            if (nameParts.length >= 2) {
              questionnaireData.piFirstName = nameParts[0];
              questionnaireData.piLastName = nameParts[nameParts.length - 1];
              
              // If there are 3 or more parts, treat everything in the middle as the middle name
              if (nameParts.length > 2) {
                questionnaireData.piMiddleName = nameParts.slice(1, nameParts.length - 1).join(' ');
              }
            }
          }

          if (questionnaireData.primaryPerformanceSite && !questionnaireData.projectSites) {
            // Try to extract structured data if it exists in JSON format
            try {
              const parsedSites = JSON.parse(questionnaireData.primaryPerformanceSite);
              if (Array.isArray(parsedSites)) {
                questionnaireData.projectSites = parsedSites;
              }
            } catch (e) {
              // If not JSON, create a placeholder with the text value
              questionnaireData.projectSites = [{
                organizationName: questionnaireData.primaryPerformanceSite.trim(),
                ueiNumber: '',
                street1: '',
                street2: '',
                city: '',
                state: '',
                zipCode: '',
                congressionalDistrict: '',
                isPrimary: true
              }];
            }
          }
          
          // Ensure at least one project site exists
          if (!questionnaireData.projectSites || !Array.isArray(questionnaireData.projectSites) || questionnaireData.projectSites.length === 0) {
            questionnaireData.projectSites = [{
              organizationName: '',
              ueiNumber: '',
              street1: '',
              street2: '',
              city: '',
              state: '',
              zipCode: '',
              congressionalDistrict: '',
              isPrimary: true
            }];
          }

          // console.log("formdata: ", formData);
          // console.log("questionnairedata: ", questionnaireData);
          
          // Functional update: `formData` captured in this closure is the value from
          // when the effect ran, so spreading it would undo anything the concurrent
          // company-info autofill has already written (e.g. the primary site UEI).
          setFormData(prev => ({
            ...prev,
            ...applicationData,
            ...questionnaireData
          }));
        } else {
          // No questionnaire data yet, just use application data
          setFormData(prev => ({
            ...prev,
            ...applicationData
          }));
        }
      } catch (error) {
        console.error('Error fetching application data:', error);
        setError('Failed to load application data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (applicationId) {
      fetchApplicationData();
      fetchSavedSites();
      fetchCompanyInfoAutofill();
      fetchUeiFromRegistrations();
    }
  }, [applicationId]);

  // State for the autofill source from the company info page (used to autofill
  // the primary performance site organization name + address + UEI).
  const [companyInfoAutofill, setCompanyInfoAutofill] = useState(null);
  // UEI as shown on the Registrations page, used to autofill the primary site.
  const [registrationsUei, setRegistrationsUei] = useState('');
  // Signing official assigned on the super admin dashboard, used as the fallback
  // for the contact/authorised representative blocks on the R&R Cover.
  const [companyInfoMeta, setCompanyInfoMeta] = useState({ soAor: '', soAorEmail: '' });

  // R&R Cover applicant box: pre-filled and read-only until Edit is pressed, and
  // saving pushes the change back to the Company Information page.
  const [applicantBoxOpen, setApplicantBoxOpen] = useState(true);
  const [authRepBoxOpen, setAuthRepBoxOpen] = useState(false);
  const [applicantEditing, setApplicantEditing] = useState(false);
  const [applicantDraft, setApplicantDraft] = useState(null);
  const [applicantConfirm, setApplicantConfirm] = useState(null); // list of pending changes
  const [savingApplicant, setSavingApplicant] = useState(false);

  // Name of the client company, used to label the primary performance site and in
  // outbound sub-contract emails. Falls back to the primary site's organization name.
  const companyDisplayName =
    companyInfoAutofill?.companyName ||
    formData.projectSites?.[0]?.organizationName ||
    '';

  // The applicant details shown on the R&R Cover, all sourced from the company
  // record with the signing official's assigned name/email as the fallback.
  // The company's business address for grant forms: whichever address is
  // checked as the Grants.gov address on the Company Information page.
  const companyBusinessAddress = (() => {
    const ci = companyInfoAutofill || {};
    const a = ci.grantsGovAddress || {};
    return {
      street1: a.line1 || ci.businessAddressLine1 || '',
      street2: a.line2 || ci.businessAddressLine2 || '',
      city: a.city || ci.businessAddressCity || '',
      state: a.state || ci.businessAddressState || '',
      zipCode: a.zip || ci.businessAddressZip || '',
      congressionalDistrict: a.congressionalDistrict || ci.businessAddressCongressionalDistrict || ''
    };
  })();

  const applicantValues = (() => {
    const ci = companyInfoAutofill || {};
    // The signing official's name always comes from the name assigned on the
    // super admin dashboard. Older records stored the SO email in the first-name
    // field, so the per-company first/last fields are no longer read.
    const soName = String(companyInfoMeta.soAor || '').trim();
    const soParts = soName.includes('@') ? [] : soName.split(/\s+/).filter(Boolean);
    return {
      ueiNumber: ci.ueiNumber || registrationsUei || '',
      companyName: ci.companyName || '',
      businessAddressLine1: companyBusinessAddress.street1,
      businessAddressCity: companyBusinessAddress.city,
      businessAddressZip: companyBusinessAddress.zipCode,
      signingOfficialFirstName: soParts[0] || '',
      signingOfficialLastName: soParts.slice(1).join(' '),
      signingOfficialPhone: ci.signingOfficialPhone || '',
      signingOfficialEmail: ci.signingOfficialEmail || companyInfoMeta.soAorEmail || '',
      einNumber: ci.einNumber || '',
      businessType: ci.businessType || DEFAULT_BUSINESS_TYPE,
      businessTypeOther: ci.businessTypeOther || '',
      smallBusinessWomanOwned: !!ci.smallBusinessWomanOwned,
      smallBusinessDisadvantaged: !!ci.smallBusinessDisadvantaged
    };
  })();

  // Everything in the applicant box is required before the questionnaire can be
  // marked complete, since the R&R Cover cannot be filed without it.
  const missingApplicantFields = APPLICANT_FIELDS.filter(
    ({ field }) => !String(applicantValues[field] || '').trim()
  );

  const applicantFieldValue = (field) =>
    (applicantEditing && applicantDraft ? applicantDraft[field] : applicantValues[field]);

  const startApplicantEdit = () => {
    setApplicantDraft({ ...applicantValues });
    setApplicantEditing(true);
  };

  const cancelApplicantEdit = () => {
    setApplicantDraft(null);
    setApplicantEditing(false);
    setApplicantConfirm(null);
  };

  const handleApplicantDraftChange = (field, value) => {
    setApplicantDraft(prev => ({ ...(prev || applicantValues), [field]: value }));
  };

  // Collect what actually changed so the confirmation can name it, then ask before
  // writing anything back to the company record.
  const requestApplicantSave = () => {
    const draft = applicantDraft || {};
    const changes = Object.keys(applicantValues)
      .filter(field => String(draft[field] ?? '') !== String(applicantValues[field] ?? ''))
      .map(field => ({
        field,
        label: (APPLICANT_FIELDS.find(f => f.field === field) || {}).label || field,
        from: applicantValues[field],
        to: draft[field]
      }));

    if (changes.length === 0) {
      cancelApplicantEdit();
      return;
    }
    setApplicantConfirm(changes);
  };

  // Persist the applicant box back to the Company Information page. Every field in
  // it lives there, so the two can never drift apart.
  const confirmApplicantSave = async () => {
    setSavingApplicant(true);
    try {
      // The signing official's name and phone aren't editable here, so they're
      // left out of what is written back.
      // eslint-disable-next-line no-unused-vars
      const {
        signingOfficialFirstName, signingOfficialLastName, signingOfficialPhone,
        businessAddressLine1, businessAddressCity, businessAddressZip,
        ...draft
      } = applicantDraft || {};
      const ci = companyInfoAutofill || {};
      const addressPatch = { line1: businessAddressLine1, city: businessAddressCity, zip: businessAddressZip };
      // The address shown here is the Grants.gov address, which may be one of the
      // additional business addresses rather than the primary one.
      const grantsGovIndex = Number(ci.grantsGovAddressIndex) || 0;
      const addressFields = grantsGovIndex > 0
        ? {
            additionalBusinessAddresses: (ci.additionalBusinessAddresses || []).map((addr, i) => (
              i === grantsGovIndex - 1 ? { ...addr, ...addressPatch } : addr
            ))
          }
        : {
            businessAddressLine1: addressPatch.line1,
            businessAddressCity: addressPatch.city,
            businessAddressZip: addressPatch.zip
          };
      const payload = {
        companyInfo: {
          ...ci,
          ...draft,
          ...addressFields
        }
      };
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update the Company Information page');
      }
      const data = await resp.json();
      setCompanyInfoAutofill(data.companyInfo || payload.companyInfo);
      cancelApplicantEdit();
    } catch (e) {
      console.error('Error saving applicant information:', e);
      alert(e.message || 'Failed to update the Company Information page.');
    } finally {
      setSavingApplicant(false);
    }
  };

  // The R&R Cover congressional district is the business (Grants.gov) address's
  // district, so changing it here is written back to the Company Information page.
  const syncCongressionalDistrictToCompanyInfo = async (value) => {
    const ci = companyInfoAutofill || {};
    const next = String(value || '').trim();
    if (!next || next === String(companyBusinessAddress.congressionalDistrict || '').trim()) return;
    if (!window.confirm(
      `Change the congressional district to "${next}"? This also updates the business address on your Company Information page.`
    )) return;
    const grantsGovIndex = Number(ci.grantsGovAddressIndex) || 0;
    const patch = grantsGovIndex > 0
      ? {
          additionalBusinessAddresses: (ci.additionalBusinessAddresses || []).map((addr, i) => (
            i === grantsGovIndex - 1 ? { ...addr, congressionalDistrict: next } : addr
          ))
        }
      : { businessAddressCongressionalDistrict: next };
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyInfo: { ...ci, ...patch }
        })
      });
      if (resp.ok) {
        const data = await resp.json();
        setCompanyInfoAutofill(data.companyInfo || { ...ci, ...patch });
      }
    } catch (e) {
      console.error('Error saving congressional district to company info:', e);
    }
  };

  // --- Program income budget periods (Cover Page Supplement) ---
  const addProgramIncomePeriod = () => {
    setFormData(prev => ({
      ...prev,
      programIncomePeriods: [
        ...(prev.programIncomePeriods || []),
        { budgetPeriod: '', anticipatedAmount: '', sources: '' }
      ]
    }));
  };

  const updateProgramIncomePeriod = (index, field, value) => {
    setFormData(prev => {
      const rows = [...(prev.programIncomePeriods || [])];
      rows[index] = { ...rows[index], [field]: value };
      return { ...prev, programIncomePeriods: rows };
    });
  };

  const removeProgramIncomePeriod = (index) => {
    setFormData(prev => {
      const rows = [...(prev.programIncomePeriods || [])];
      rows.splice(index, 1);
      return { ...prev, programIncomePeriods: rows };
    });
  };

  // --- Senior/key personnel (section 6) ---
  const addKeyPerson = () => {
    setFormData(prev => ({
      ...prev,
      keyPersonnel: [...(prev.keyPersonnel || []), { ...EMPTY_KEY_PERSON }]
    }));
  };

  const removeKeyPerson = (index) => {
    setFormData(prev => {
      const people = [...(prev.keyPersonnel || [])];
      people.splice(index, 1);
      return { ...prev, keyPersonnel: people };
    });
  };

  const handleKeyPersonChange = (index, field, value) => {
    setFormData(prev => {
      const people = [...(prev.keyPersonnel || [])];
      people[index] = { ...people[index], [field]: value };

      // The first entry is the PI; the rest of the app still reads the flat
      // pi* fields, so keep them in step.
      const mirrored = index === 0
        ? {
            piFirstName: field === 'firstName' ? value : prev.piFirstName,
            piLastName: field === 'lastName' ? value : prev.piLastName
          }
        : {};

      // Selecting the client company fills the business address from the company
      // record; any other organization is completed on the Sub-Contract page.
      if (field === 'organizationName') {
        const isClientCompany = value && value === companyDisplayName;
        if (isClientCompany) {
          people[index] = {
            ...people[index],
            street1: companyBusinessAddress.street1,
            street2: companyBusinessAddress.street2,
            city: companyBusinessAddress.city,
            state: companyBusinessAddress.state,
            zipCode: companyBusinessAddress.zipCode,
            country: 'UNITED STATES'
          };
        } else {
          people[index] = {
            ...people[index],
            street1: '', street2: '', city: '', state: '', zipCode: '', country: 'UNITED STATES'
          };
        }
      }
      return { ...prev, ...mirrored, keyPersonnel: people };
    });
  };

  // Organizations a key person can belong to: the client company plus whatever
  // was entered as a performance site. A site named like the company is listed once.
  const keyPersonOrganizations = Array.from(new Set(
    [companyDisplayName, ...(formData.projectSites || []).map(site => site.organizationName)]
      .map(name => (name || '').trim())
      .filter(Boolean)
  ));

  const fetchCompanyInfoAutofill = async () => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const ci = data.companyInfo || {};
      setCompanyInfoAutofill(ci);
      setCompanyInfoMeta({ soAor: data.soAor || '', soAorEmail: data.soAorEmail || '' });
      applyCompanyInfoToPrimarySite(ci);
    } catch (e) {
      console.error('Error autofilling questionnaire from company info:', e);
    }
  };

  // Read the UEI straight from the Registrations data source (/files/existing-files),
  // matching the same way Registrations.js getLatestFile() does: by documentType
  // metadata, exact filename, or a "UEI.pdf"-style name. This is the number shown on
  // the Registrations page, so it is the value users expect here.
  const fetchUeiFromRegistrations = async () => {
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/files/existing-files`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!resp.ok) return;

      const files = await resp.json();

      const matches = (files || []).filter(f =>
        f.documentType === 'UEI' ||
        f.documentName === 'UEI' ||
        (f.documentName && f.documentName.startsWith('UEI.'))
      );

      const latest = matches.reduce((best, f) =>
        !best || new Date(f.lastModified) > new Date(best.lastModified) ? f : best
      , null);

      const uei = String(latest?.documentNumber || '').trim();

      // getExistingFiles returns the literal 'N/A' when no number was recorded.
      if (!uei || uei === 'N/A') return;

      setRegistrationsUei(uei);
      setFormData(prev => {
        const sites = [...(prev.projectSites || [])];
        if (sites.length === 0) return prev;
        if (sites[0].ueiNumber) return prev; // never overwrite what the user typed
        sites[0] = { ...sites[0], ueiNumber: uei };
        return { ...prev, projectSites: sites };
      });
    } catch (e) {
      console.error('Error reading UEI from registrations:', e);
    }
  };

  // Fill blank primary-site fields from the company record. Only ever fills empties,
  // so it is safe to run more than once.
  const applyCompanyInfoToPrimarySite = (ci) => {
    if (!ci) return;
    setFormData(prev => {
        const sites = Array.isArray(prev.projectSites) && prev.projectSites.length > 0
          ? [...prev.projectSites]
          : [{
              organizationName: '', ueiNumber: '', street1: '', street2: '',
              city: '', state: '', zipCode: '', congressionalDistrict: '',
              isPrimary: true, hasSubcontractedWork: 'No', willReceiveSubcontract: '', subcontractContactEmail: '',
              subcontractPIName: '', subcontractPIEmail: '', subcontractBusinessOfficeEmail: ''
            }];
        const primary = { ...sites[0] };
        // Choose performance-site address if defined, else business address.
        const useSame = !!ci.performanceSiteSameAsBusiness;
        const addr = useSame
          ? {
              line1: ci.businessAddressLine1, line2: ci.businessAddressLine2,
              city: ci.businessAddressCity, state: ci.businessAddressState, zip: ci.businessAddressZip
            }
          : {
              line1: ci.performanceSiteAddressLine1, line2: ci.performanceSiteAddressLine2,
              city: ci.performanceSiteAddressCity, state: ci.performanceSiteAddressState, zip: ci.performanceSiteAddressZip
            };
        if (!primary.organizationName) primary.organizationName = ci.companyName || '';
        if (!primary.ueiNumber && ci.ueiNumber) primary.ueiNumber = ci.ueiNumber;
        if (!primary.street1 && addr.line1) primary.street1 = addr.line1;
        if (!primary.street2 && addr.line2) primary.street2 = addr.line2;
        if (!primary.city && addr.city) primary.city = addr.city;
        if (!primary.state && addr.state) primary.state = addr.state;
        if (!primary.zipCode && addr.zip) primary.zipCode = addr.zip;
        const siteDistrict = ci.performanceSiteCongressionalDistrict
          || (useSame ? ci.businessAddressCongressionalDistrict : '');
        if (!primary.congressionalDistrict && siteDistrict) primary.congressionalDistrict = siteDistrict;
        sites[0] = primary;
        return { ...prev, projectSites: sites };
    });
  };

  // The questionnaire fetch and the company-info fetch run concurrently, and the
  // questionnaire's setFormData replaces projectSites wholesale. Re-apply the company
  // defaults once loading settles so the UEI/address land no matter which finished
  // last. Filling only empty fields makes the repeat harmless.
  useEffect(() => {
    if (isLoading) return;
    if (!companyInfoAutofill) return;
    if (questionnaireMeta.isComplete) return; // locked form: don't rewrite submitted answers
    applyCompanyInfoToPrimarySite(companyInfoAutofill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, companyInfoAutofill, questionnaireMeta.isComplete]);

  // "Are Vertebrate Animals Used?" in section 4 is the same answer as the
  // Cover Page Supplement question, so mirror it rather than asking twice.
  useEffect(() => {
    setFormData(prev => (
      prev.vertebrateAnimalsUsed === prev.vertebrateAnimalsInvolved
        ? prev
        : { ...prev, vertebrateAnimalsUsed: prev.vertebrateAnimalsInvolved }
    ));
  }, [formData.vertebrateAnimalsInvolved]);

  // Seed the answers that are simply copies of the company record: the primary
  // site's congressional district, and the PI row of the key personnel table.
  useEffect(() => {
    if (isLoading || questionnaireMeta.isComplete) return;
    setFormData(prev => {
      let next = prev;
      // The R&R Cover district is the business address's district.
      const district = companyBusinessAddress.congressionalDistrict;
      if (district && prev.congressionalDistrict !== district) {
        next = { ...next, congressionalDistrict: district };
      }
      const businessAddressFields = {
        street1: companyBusinessAddress.street1,
        street2: companyBusinessAddress.street2,
        city: companyBusinessAddress.city,
        state: companyBusinessAddress.state,
        zipCode: companyBusinessAddress.zipCode,
        country: 'UNITED STATES'
      };
      if (!Array.isArray(prev.keyPersonnel) || prev.keyPersonnel.length === 0) {
        next = {
          ...next,
          keyPersonnel: [{
            ...EMPTY_KEY_PERSON,
            firstName: prev.piFirstName || '',
            lastName: prev.piLastName || '',
            projectRole: 'PI',
            organizationName: companyDisplayName,
            ...businessAddressFields
          }]
        };
      } else if (companyDisplayName) {
        // The lead investigator always sits at the client company, and everyone at
        // the client company uses its business address.
        const people = prev.keyPersonnel.map((person, i) => {
          const atCompany = i === 0 || person.organizationName === companyDisplayName;
          if (!atCompany) return person;
          const updated = { ...person, ...businessAddressFields, organizationName: i === 0 ? companyDisplayName : person.organizationName };
          return Object.keys(updated).some(k => updated[k] !== person[k]) ? updated : person;
        });
        if (people.some((p, i) => p !== prev.keyPersonnel[i])) {
          next = { ...next, keyPersonnel: people };
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, companyInfoAutofill, questionnaireMeta.isComplete]);

  // Same re-apply pass for the Registrations UEI, since the questionnaire fetch can
  // land after it and replace projectSites wholesale.
  useEffect(() => {
    if (isLoading || !registrationsUei || questionnaireMeta.isComplete) return;
    setFormData(prev => {
      const sites = [...(prev.projectSites || [])];
      if (sites.length === 0 || sites[0].ueiNumber) return prev;
      sites[0] = { ...sites[0], ueiNumber: registrationsUei };
      return { ...prev, projectSites: sites };
    });
  }, [isLoading, registrationsUei, questionnaireMeta.isComplete]);

  // Save the primary performance site back into the company info record.
  // Only used when the company info page does not yet have performance site info.
  const handleSavePrimarySiteToCompanyInfo = async () => {
    try {
      const primary = (formData.projectSites || [])[0];
      if (!primary) {
        alert('No primary performance site to save.');
        return;
      }
      const ci = { ...(companyInfoAutofill || {}) };
      const payload = {
        companyInfo: {
          ...ci,
          performanceSiteSameAsBusiness: false,
          performanceSiteAddressLine1: primary.street1 || '',
          performanceSiteAddressLine2: primary.street2 || '',
          performanceSiteAddressCity: primary.city || '',
          performanceSiteAddressState: primary.state || '',
          performanceSiteAddressZip: primary.zipCode || '',
          performanceSiteAddress: [
            [primary.street1, primary.street2].filter(Boolean).join(', '),
            [primary.city, [primary.state, primary.zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ')
          ].filter(Boolean).join(', ')
        }
      };
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/company-formation/company-info`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (resp.ok) {
        const data = await resp.json();
        setCompanyInfoAutofill(data.companyInfo || payload.companyInfo);
        alert('Performance site saved to Company Info.');
      } else {
        const err = await resp.json().catch(() => ({}));
        alert(err.error || 'Failed to save performance site to company info.');
      }
    } catch (e) {
      console.error('Error saving performance site to company info:', e);
      alert('Failed to save performance site to company info.');
    }
  };

  // control field behavior based on program type
  useEffect(() => {
    if (formData.programType === "SBIR") {
      // Reset STTR-specific fields if switching to SBIR-only
      setFormData(prevFormData => ({
        ...prevFormData,
        piCommitmentAndEffort: 'Yes',
        workDistributionRequirementMet: 'Yes',
        nonprofitResearchPartnerUEI: ''
      }));
    } else if (formData.programType === "STTR") {
      // Reset SBIR-specific fields if switching to STTR-only
      setFormData(prevFormData => ({
        ...prevFormData,
        receivedSBIRPhaseII: 'No',
        piPrimaryEmploymentWithSmallBusiness: 'Yes'
      }));
    }
    // Both program type should keep all fields enabled
  }, [formData.programType]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Total Federal & Non-Federal funds is derived, never entered.
  const totalProjectFunding = (() => {
    const federal = parseFloat(formData.totalFederalFunds);
    const nonFederal = parseFloat(formData.totalNonFederalFunds);
    if (Number.isNaN(federal) && Number.isNaN(nonFederal)) return '';
    return ((federal || 0) + (nonFederal || 0)).toFixed(2);
  })();

  // Handle checkbox changes
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      [name]: checked
    });
  };

  // Handle radio button changes
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Add this new handler for vertebrate animals
  const handleVertebrateAnimalsChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Handle subcontract email preview for a single site
  const handleSubcontractEmailPreview = async (siteIndex) => {
    try {
      const site = formData.projectSites[siteIndex];

      if (!site.subcontractPIEmail || !site.subcontractPIName || !site.organizationName) {
        setError('Please enter the organization name, sub-contract PI name, and sub-contract PI email before sending the request.');
        setTimeout(() => setError(null), 4000);
        return;
      }

      // Business office is CC'd when known.
      const cc = site.subcontractBusinessOfficeEmail ? [site.subcontractBusinessOfficeEmail] : [];
      // PI at the client company — appears in the first line of the email.
      const piName = [formData.piFirstName, formData.piMiddleName, formData.piLastName]
        .filter(Boolean)
        .join(' ')
        .trim();

      // First generate the token to get the upload URL
      const tokenResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/subcontract/generate-token`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          applicationId,
          siteIndex,
          contactEmail: site.subcontractPIEmail,
          contactName: site.subcontractPIName,
          siteName: site.organizationName,
          piName,
          ccEmails: cc,
          // companyName will be determined from session in backend
          skipEmail: true // Don't send email yet, just generate token
        })
      });

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        console.error('Token generation error:', errorData);
        throw new Error(`Failed to generate token for site: ${site.organizationName}`);
      }

      const tokenData = await tokenResponse.json();

      // Prepare variables for the email template
      setSubcontractEmailVariables({
        contactName: site.subcontractPIName,
        siteName: site.organizationName,
        grantTitle: application.name,
        paRfaNumber: tokenData.paRfaNumber || application.paRfaNumber || '[PA/RFA number]',
        piName,
        companyName: tokenData.companyName || companyDisplayName || 'Your Company', // Get from token response
        companyUEI: tokenData.companyUEI || '[To be provided]',
        companyAddress: tokenData.companyAddress || '[To be provided]',
        uploadUrl: tokenData.uploadUrl,
        // Contact clause pointing at the company's assigned StreamLaunch admins.
        adminContact: tokenData.adminContact || ''
      });

      setSubcontractRecipientEmail(site.subcontractPIEmail);
      setSubcontractCcEmails(cc);
      setCurrentSubcontractSiteIndex(siteIndex);
      setShowSubcontractEmailPreview(true);
    } catch (error) {
      console.error('Error preparing email preview:', error);
      setError('Failed to prepare email preview');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle sending subcontract email with edited content
  const handleSendSubcontractEmails = async (emailData) => {
    try {
      setSendingSubcontractEmails(true);

      // Auto-save draft before sending the email
      try {
        await handleSaveDraft();
      } catch (saveError) {
        console.error('Error auto-saving draft:', saveError);
        // Continue with sending email even if save fails
      }

      // Send the email with edited content
      const emailResponse = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/emails/send`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          to: emailData.recipientEmail,
          cc: emailData.ccEmails && emailData.ccEmails.length > 0 ? emailData.ccEmails : undefined,
          subject: emailData.subject,
          html: emailData.htmlContent,
          emailType: 'Subcontract Document Request'
        })
      });

      if (!emailResponse.ok) {
        throw new Error('Failed to send email');
      }

      // Mark this site as having sent a request
      setSentSubcontractRequests(prev => new Set([...prev, currentSubcontractSiteIndex]));

      // Close modal and show success
      setShowSubcontractEmailPreview(false);
      setSaveSuccess(true);
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);

    } catch (error) {
      console.error('Error sending subcontract email:', error);
      setError(`Failed to send subcontract request: ${error.message}`);
      setTimeout(() => setError(null), 5000);
    } finally {
      setSendingSubcontractEmails(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // The R&R Cover cannot be filed without the applicant details, and they live
    // on the Company Information page, so submission waits until they are there.
    if (missingApplicantFields.length > 0) {
      setError(
        'The following R&R Cover applicant details are still missing: '
        + missingApplicantFields.map(f => f.label).join(', ')
        + '. Fill them in under Applicant Information (or on the Company Information page) before submitting.'
      );
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setIsSaving(true);
      setSaveSuccess(false);
      setError(null);

      const submitData = { ...formData };
    
      // Create a combined piName field for backward compatibility
      submitData.piName = [
        submitData.piFirstName,
        submitData.piMiddleName,
        submitData.piLastName
      ].filter(Boolean).join(' ');

      const primarySite = submitData.projectSites.find(site => site.isPrimary) || submitData.projectSites[0];
      submitData.primaryPerformanceSite = `${primarySite.organizationName || ''}\n${primarySite.street1 || ''} ${primarySite.street2 ? primarySite.street2 + '\n' : '\n'}${primarySite.city || ''}, ${primarySite.state || ''} ${primarySite.zipCode || ''}`;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/questionnaire`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: submitData, isComplete: true })
      });

      if (!response.ok) {
        throw new Error('Failed to save questionnaire data');
      }

      setSaveSuccess(true);
      // Mark as complete locally so the form locks immediately.
      setQuestionnaireMeta({ isComplete: true, completedAt: new Date().toISOString() });

      // Performance sites are now automatically available for autofill from this submission
      console.log('Questionnaire submitted - refreshing saved sites for autofill');

      // Refetch saved sites so they're immediately available for autofill
      setTimeout(() => {
        fetchSavedSites();
      }, 1000); // Small delay to ensure the questionnaire is fully saved

      // Optionally navigate after successful save
      // setTimeout(() => navigate(`/grant-submission`), 2000);
    } catch (error) {
      console.error('Error saving questionnaire data:', error);
      setError('Failed to save questionnaire data. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      setSaveSuccess(false);
      setError(null);

      const draftData = { ...formData };
      
      // Create a combined piName field for backward compatibility
      draftData.piName = [
        draftData.piFirstName,
        draftData.piMiddleName,
        draftData.piLastName
      ].filter(Boolean).join(' ');

      const primarySite = draftData.projectSites.find(site => site.isPrimary) || draftData.projectSites[0];
      draftData.primaryPerformanceSite = `${primarySite.organizationName || ''}\n${primarySite.street1 || ''} ${primarySite.street2 ? primarySite.street2 + '\n' : '\n'}${primarySite.city || ''}, ${primarySite.state || ''} ${primarySite.zipCode || ''}`;
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grant-applications/${applicationId}/questionnaire`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ answers: draftData, isDraft: true })
      });

      if (!response.ok) {
        throw new Error('Failed to save draft');
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
      // Performance sites are now automatically available for autofill from this submission
      console.log('Draft saved - refreshing saved sites for autofill');
      
      // Refetch saved sites so they're immediately available for autofill
      setTimeout(() => {
        fetchSavedSites();
      }, 1000); // Small delay to ensure the draft is fully saved
    } catch (error) {
      console.error('Error saving draft:', error);
      setError('Failed to save draft. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  // The sticky header's height drives both the table-of-contents offset and the
  // scroll-margin on each section, so measure it rather than hard-coding a value
  // (a long application title can wrap onto a second line).
  const stickyHeaderRef = useRef(null);

  useEffect(() => {
    const header = stickyHeaderRef.current;
    if (!header) return;

    const applyOffset = () => {
      const navHeight = 64; // StreamLaunch nav bar (h-16)
      document.documentElement.style.setProperty(
        '--questionnaire-header-offset',
        `${Math.round(header.getBoundingClientRect().height) + navHeight}px`
      );
    };

    applyOffset();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', applyOffset);
      return () => window.removeEventListener('resize', applyOffset);
    }

    const observer = new ResizeObserver(applyOffset);
    observer.observe(header);
    return () => observer.disconnect();
  });

  const handleReturn = () => {
    navigate('/grant-submission');
  };

  if (isLoading) {
    return <div className="loading-container">Loading application data...</div>;
  }

  if (error && !application) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="return-btn" onClick={handleReturn}>Return to Applications</button>
      </div>
    );
  }

  return (
    <div className="grant-questionnaire-container">
      {/* Sticky page header: stays visible (below the StreamLaunch nav) while scrolling */}
      <div className="questionnaire-sticky-header" ref={stickyHeaderRef}>
        <div className="questionnaire-header">
          <h1>Grant Application Questionnaire</h1>
          <div className="questionnaire-header-actions">
            {/* The same Save Draft / Mark as Complete controls as the bottom of the
                form, repeated in the sticky header so they're reachable from any
                point in this very long questionnaire. They target the form by id
                so they keep working from outside it. */}
            <button
              type="button"
              className="save-draft-btn header-action-btn"
              onClick={handleSaveDraft}
              disabled={isSaving || questionnaireMeta.isComplete}
              title={questionnaireMeta.isComplete ? 'Unlock the questionnaire to make changes' : ''}
            >
              {isSaving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              type="submit"
              form="grant-questionnaire-form"
              className="save-btn mark-complete-btn header-action-btn"
              disabled={isSaving || questionnaireMeta.isComplete}
              title={questionnaireMeta.isComplete ? 'Already marked complete — unlock to re-submit' : ''}
            >
              {isSaving ? 'Saving...' : 'Mark as Complete'}
            </button>
            {questionnaireMeta.isComplete && (
              <button
                type="button"
                className="unlock-btn"
                onClick={() => setShowUnlockModal(true)}
              >
                Unlock &amp; Modify
              </button>
            )}
            <button className="return-btn" onClick={handleReturn}>
              Return to Applications
            </button>
          </div>
        </div>

        <div className="application-info-bar">
          <h2>{application?.name}</h2>
        </div>
      </div>

      {/* Layout with sticky sidebar and scrollable content */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Sticky Table of Contents Sidebar */}
        <div className="table-of-contents-sidebar" style={{
          position: 'sticky',
          top: 'calc(var(--questionnaire-header-offset) + 20px)',
          flex: '0 0 280px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '20px',
          maxHeight: 'calc(100vh - var(--questionnaire-header-offset) - 60px)',
          overflowY: 'auto'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '4px', color: '#1f2937', fontSize: '16px', fontWeight: '600' }}>
            Table of Contents
          </h3>
          <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
            All sections below are on this same page — click a link to jump to that section.
          </p>
          <nav>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {TOC_SECTIONS.map(item => (
                <li key={item.id} style={{ marginBottom: '8px' }}>
                  <button
                    onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' })}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      textDecoration: 'none',
                      fontSize: '13px',
                      display: 'block',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s',
                      cursor: 'pointer',
                      width: '100%',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#e0f2fe'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Scrollable Form Content */}
        <div style={{ flex: '1', minWidth: 0 }}>
          <form id="grant-questionnaire-form" className="questionnaire-form" onSubmit={handleSubmit}>

        {/* When the questionnaire is marked complete, lock every field until it is
            explicitly unlocked. The fieldset is disabled for all roles (admins included);
            the "Unlock & Modify" control lives outside this fieldset so it stays clickable. */}
        <fieldset
          disabled={questionnaireMeta.isComplete}
          style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}
        >

        <section className="form-section" id="basic-information">
          <h3>1. Basic Information</h3>
          
          <div className="form-group">
            <label htmlFor="rfaNumber" className="required-indicator">To which PA or RFA are you applying?</label>
            <input
              type="text"
              id="rfaNumber"
              name="rfaNumber"
              value={formData.rfaNumber}
              onChange={handleInputChange}
              required
              readOnly={application && application.paRfaNumber}
              style={application && application.paRfaNumber ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
            />
            {application && application.paRfaNumber && (
              <small style={{ color: '#666', fontSize: '12px' }}>
                This field was pre-filled from the grant application data and cannot be edited.
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="proposalTitle" className="required-indicator">What is the title of your proposal?</label>
            <input
              type="text"
              id="proposalTitle"
              name="proposalTitle"
              value={formData.proposalTitle}
              onChange={handleInputChange}
              required
              readOnly={application && application.name}
              style={application && application.name ? { backgroundColor: '#f0f0f0', cursor: 'not-allowed' } : {}}
            />
            {application && application.name && (
              <small style={{ color: '#666', fontSize: '12px' }}>
                This field was pre-filled from the grant application data and cannot be edited.
              </small>
            )}
          </div>

        </section>

        {/* ===== 2. R&R Cover ===== */}
        <section className="form-section" id="rr-cover">
          <h3>2. R&amp;R Cover</h3>

          <div className="form-group">
            <label htmlFor="typeOfSubmission" className="required-indicator">TYPE OF SUBMISSION</label>
            <select
              id="typeOfSubmission"
              name="typeOfSubmission"
              value={formData.typeOfSubmission || 'Application'}
              onChange={handleInputChange}
              required
            >
              {SUBMISSION_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Applicant information, employer identification and the contact person
              are all copies of the company record, so they sit in one collapsed box
              and are read-only until Edit is pressed. */}
          <div className="prefilled-box">
            <div className="prefilled-box-header">
              {/* Not a <button>: the locked questionnaire disables its fieldset, which
                  disables every button inside, but the box should still collapse. */}
              <span
                role="button"
                tabIndex={0}
                className="prefilled-box-toggle"
                aria-expanded={applicantBoxOpen}
                onClick={() => setApplicantBoxOpen(open => !open)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setApplicantBoxOpen(open => !open); } }}
              >
                <span className="toggle-icon">{applicantBoxOpen ? '▼' : '►'}</span>
                APPLICANT INFORMATION
              </span>
              {applicantBoxOpen && (
                applicantEditing ? (
                  <span className="prefilled-box-actions">
                    <button type="button" className="autofill-btn" onClick={requestApplicantSave}>
                      Save Changes
                    </button>
                    <button type="button" className="cancel-autofill" onClick={cancelApplicantEdit}>
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button type="button" className="autofill-btn" onClick={startApplicantEdit}>
                    Edit
                  </button>
                )
              )}
            </div>

            {applicantBoxOpen && (
              <div className="prefilled-box-body">
                <p className="section-note">
                  Pre-filled from your Company Information page. Editing an answer here also
                  updates it there.
                </p>

                {missingApplicantFields.length > 0 && (
                  <p className="prefilled-missing">
                    Required and still missing: {missingApplicantFields.map(f => f.label).join(', ')}.
                  </p>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="rr-uei">UEI:</label>
                    <input
                      type="text"
                      id="rr-uei"
                      value={applicantFieldValue('ueiNumber') || ''}
                      onChange={(e) => handleApplicantDraftChange('ueiNumber', e.target.value)}
                      readOnly={!applicantEditing}
                      style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rr-legal-name">Legal Business Name:</label>
                    <input
                      type="text"
                      id="rr-legal-name"
                      value={applicantFieldValue('companyName') || ''}
                      onChange={(e) => handleApplicantDraftChange('companyName', e.target.value)}
                      readOnly={!applicantEditing}
                      style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="rr-street">Street:</label>
                  <input
                    type="text"
                    id="rr-street"
                    value={applicantFieldValue('businessAddressLine1') || ''}
                    onChange={(e) => handleApplicantDraftChange('businessAddressLine1', e.target.value)}
                    readOnly={!applicantEditing}
                    style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="rr-city">City:</label>
                    <input
                      type="text"
                      id="rr-city"
                      value={applicantFieldValue('businessAddressCity') || ''}
                      onChange={(e) => handleApplicantDraftChange('businessAddressCity', e.target.value)}
                      readOnly={!applicantEditing}
                      style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rr-country">Country:</label>
                    <input type="text" id="rr-country" value="UNITED STATES" readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rr-zip">Zip/Postal Code:</label>
                    <input
                      type="text"
                      id="rr-zip"
                      placeholder="#####-####"
                      value={applicantFieldValue('businessAddressZip') || ''}
                      onChange={(e) => handleApplicantDraftChange('businessAddressZip', e.target.value)}
                      readOnly={!applicantEditing}
                      style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                    />
                  </div>
                </div>

                <h4 className="subsection-title">
                  Person to be contacted on matters involving this application (Signing Official)
                </h4>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="rr-so-first">First Name:</label>
                    <input
                      type="text"
                      id="rr-so-first"
                      value={applicantValues.signingOfficialFirstName || ''}
                      readOnly
                      style={{ backgroundColor: '#f0f0f0' }}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rr-so-last">Last Name:</label>
                    <input
                      type="text"
                      id="rr-so-last"
                      value={applicantValues.signingOfficialLastName || ''}
                      readOnly
                      style={{ backgroundColor: '#f0f0f0' }}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="rr-so-phone">Phone Number:</label>
                    <input
                      type="tel"
                      id="rr-so-phone"
                      value={applicantValues.signingOfficialPhone || ''}
                      readOnly
                      style={{ backgroundColor: '#f0f0f0' }}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="rr-so-email">Email:</label>
                    <input
                      type="email"
                      id="rr-so-email"
                      value={applicantFieldValue('signingOfficialEmail') || ''}
                      onChange={(e) => handleApplicantDraftChange('signingOfficialEmail', e.target.value)}
                      readOnly={!applicantEditing || !isAdmin}
                      style={!applicantEditing || !isAdmin ? { backgroundColor: '#f0f0f0' } : {}}
                    />
                  </div>
                </div>
                <p className="section-note">
                  The signing official's name is assigned by StreamLaunch and the phone is
                  StreamLaunch's number; only StreamLaunch admins can change the email. Their
                  address is the business address shown above (UNITED STATES).
                </p>

                <h4 className="subsection-title">EMPLOYER IDENTIFICATION</h4>

                <div className="form-group">
                  <label htmlFor="rr-ein">EIN:</label>
                  <input
                    type="text"
                    id="rr-ein"
                    value={applicantFieldValue('einNumber') || ''}
                    onChange={(e) => handleApplicantDraftChange('einNumber', e.target.value)}
                    readOnly={!applicantEditing}
                    style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="rr-applicant-type">Type of Applicant:</label>
                  <select
                    id="rr-applicant-type"
                    value={applicantFieldValue('businessType') || DEFAULT_BUSINESS_TYPE}
                    onChange={(e) => handleApplicantDraftChange('businessType', e.target.value)}
                    disabled={!applicantEditing}
                    style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                  >
                    {BUSINESS_TYPE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                {applicantFieldValue('businessType') === OTHER_BUSINESS_TYPE && (
                  <div className="form-group">
                    <label htmlFor="rr-applicant-type-other">Other (specify):</label>
                    <input
                      type="text"
                      id="rr-applicant-type-other"
                      value={applicantFieldValue('businessTypeOther') || ''}
                      onChange={(e) => handleApplicantDraftChange('businessTypeOther', e.target.value)}
                      readOnly={!applicantEditing}
                      style={!applicantEditing ? { backgroundColor: '#f0f0f0' } : {}}
                    />
                  </div>
                )}

                {applicantFieldValue('businessType') === SMALL_BUSINESS_TYPE && (
                  <div className="form-group">
                    <label>Small Business Organization Type:</label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!applicantFieldValue('smallBusinessWomanOwned')}
                        onChange={(e) => handleApplicantDraftChange('smallBusinessWomanOwned', e.target.checked)}
                        disabled={!applicantEditing}
                      />
                      <span>Woman Owned?</span>
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!applicantFieldValue('smallBusinessDisadvantaged')}
                        onChange={(e) => handleApplicantDraftChange('smallBusinessDisadvantaged', e.target.checked)}
                        disabled={!applicantEditing}
                      />
                      <span>Socially and Economically Disadvantaged?</span>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ---- TYPE OF APPLICATION ---- */}
          <h4 className="subsection-title">TYPE OF APPLICATION</h4>

          <div className="form-group">
            <label htmlFor="grantApplicationType" className="required-field">
              What type of grant application is this?
            </label>
            <select
              id="grantApplicationType"
              name="grantApplicationType"
              value={formData.grantApplicationType}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Select --</option>
              {GRANT_APPLICATION_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {formData.grantApplicationType === 'Resubmission' && (
            <div className="form-group">
              <label htmlFor="priorGrantNumber" className="required-field">
                Provide grant number of prior submission (e.g., 1R43AR086700-01)
              </label>
              <input
                type="text"
                id="priorGrantNumber"
                name="priorGrantNumber"
                value={formData.priorGrantNumber}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          {formData.grantApplicationType === 'Revision' && (
            <div className="form-group">
              <label>Mark appropriate box(es):</label>
              {REVISION_OPTIONS.map(option => (
                <label className="checkbox-label" key={option.field}>
                  <input
                    type="checkbox"
                    name={option.field}
                    checked={!!formData[option.field]}
                    onChange={handleInputChange}
                  />
                  <span>{option.label}</span>
                </label>
              ))}
              {formData.revisionOther && (
                <input
                  type="text"
                  name="revisionOtherSpecify"
                  value={formData.revisionOtherSpecify}
                  onChange={handleInputChange}
                  placeholder="Specify other revision"
                />
              )}
            </div>
          )}

          <div className="form-group radio-group">
            <label className="required-field">Is this application being submitted to other agencies?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input
                  type="radio"
                  name="submittedToOtherAgencies"
                  value="Yes"
                  checked={formData.submittedToOtherAgencies === 'Yes'}
                  onChange={handleRadioChange}
                  required
                />
                Yes
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="submittedToOtherAgencies"
                  value="No"
                  checked={formData.submittedToOtherAgencies === 'No'}
                  onChange={handleRadioChange}
                />
                No
              </label>
            </div>
            {formData.submittedToOtherAgencies === 'Yes' && (
              <div className="conditional-field">
                <label htmlFor="otherAgenciesNames">If yes, provide the names of the other agencies:</label>
                <textarea
                  id="otherAgenciesNames"
                  name="otherAgenciesNames"
                  value={formData.otherAgenciesNames}
                  onChange={handleInputChange}
                  rows={2}
                  required
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="rr-agency" className="required-field">Name of Federal Agency</label>
            <select
              id="rr-agency"
              name="agency"
              value={formData.agency}
              onChange={handleInputChange}
              required
            >
              <option value="">-- Select --</option>
              <option value="DOE">Department of Energy (DOE)</option>
              <option value="HHS">Health and Human Services (HHS)</option>
              <option value="USDA">U.S. Department of Agriculture (USDA)</option>
              <option value="Other">Other</option>
            </select>
            {formData.agency === 'Other' && (
              <div className="conditional-field">
                <label htmlFor="rr-agency-other">Please specify the agency:</label>
                <input
                  type="text"
                  id="rr-agency-other"
                  name="agencyOther"
                  value={formData.agencyOther}
                  onChange={handleInputChange}
                  required
                />
              </div>
            )}
          </div>

          <div className="form-group date-range">
            <label>What is the expected start and end date of the proposal?</label>
            <div className="date-inputs">
              <div className="date-field">
                <label htmlFor="startDate" className="required-indicator">Start Date:</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="date-field">
                <label htmlFor="endDate" className="required-indicator">End Date:</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="rr-congressional-district" className="required-field">
              Congressional District (
              <a href={CONGRESSIONAL_DISTRICT_URL} target="_blank" rel="noopener noreferrer">
                click here to find
              </a>
              ):
            </label>
            <input
              type="text"
              id="rr-congressional-district"
              name="congressionalDistrict"
              value={formData.congressionalDistrict}
              onChange={handleInputChange}
              onBlur={(e) => syncCongressionalDistrictToCompanyInfo(e.target.value)}
              placeholder="PA-005"
              required
            />
            <p className="section-note">
              Pre-filled from the business address (the Grants.gov address) on your Company
              Information page. Changing it here also updates it there.
            </p>
          </div>

          {/* ---- ESTIMATED PROJECT FUNDING ---- */}
          <h4 className="subsection-title">ESTIMATED PROJECT FUNDING</h4>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="totalFederalFunds">Total Federal Funds Requested ($):</label>
              <input
                type="text"
                inputMode="decimal"
                id="totalFederalFunds"
                name="totalFederalFunds"
                value={formatMoneyInput(formData.totalFederalFunds)}
                onChange={(e) => setFormData(prev => ({ ...prev, totalFederalFunds: parseMoneyInput(e.target.value) }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="totalNonFederalFunds">Total Non-Federal Funds Requested ($):</label>
              <input
                type="text"
                inputMode="decimal"
                id="totalNonFederalFunds"
                name="totalNonFederalFunds"
                value={formatMoneyInput(formData.totalNonFederalFunds)}
                onChange={(e) => setFormData(prev => ({ ...prev, totalNonFederalFunds: parseMoneyInput(e.target.value) }))}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="totalAllFunds">Total Federal &amp; Non-Federal Funds ($):</label>
              <input
                type="text"
                id="totalAllFunds"
                value={totalProjectFunding === '' ? '' : Number(totalProjectFunding).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                readOnly
                style={{ backgroundColor: '#f0f0f0' }}
              />
            </div>
            <div className="form-group">
              <label htmlFor="estimatedProgramIncome">Estimated Program Income ($):</label>
              <input
                type="text"
                inputMode="decimal"
                id="estimatedProgramIncome"
                name="estimatedProgramIncome"
                value={formatMoneyInput(formData.estimatedProgramIncome)}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedProgramIncome: parseMoneyInput(e.target.value) }))}
              />
            </div>
          </div>

          <div className="form-group radio-group">
            <label className="required-field">
              Is the applicant subject to review by the state executive order 12372 process?{' '}
              <a href={SPOC_LIST_URL} target="_blank" rel="noopener noreferrer">
                Click here to see if your state participates in this process.
              </a>
            </label>
            <p className="section-note">
              If your state is not listed (e.g., PA), select "No, the program is not covered by E.O. 12372".
            </p>
            <div className="radio-options-vertical">
              <label className="radio-label">
                <input
                  type="radio"
                  name="eo12372Status"
                  value="made_available"
                  checked={formData.eo12372Status === 'made_available'}
                  onChange={handleRadioChange}
                  required
                />
                Yes, this pre-application/application was made available to the state executive
                order 12372 process for review on:
              </label>
              {formData.eo12372Status === 'made_available' && (
                <div className="conditional-field">
                  <label htmlFor="eo12372ReviewDate">Date:</label>
                  <input
                    type="date"
                    id="eo12372ReviewDate"
                    name="eo12372ReviewDate"
                    value={formData.eo12372ReviewDate}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}
              <label className="radio-label">
                <input
                  type="radio"
                  name="eo12372Status"
                  value="not_covered"
                  checked={formData.eo12372Status === 'not_covered'}
                  onChange={handleRadioChange}
                />
                No, the program is not covered by E.O. 12372
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="eo12372Status"
                  value="not_selected"
                  checked={formData.eo12372Status === 'not_selected'}
                  onChange={handleRadioChange}
                />
                No, the program has not been selected by the state for review
              </label>
            </div>
          </div>

          {/* ---- AUTHORIZED REPRESENTATIVE ---- */}
          <div className="prefilled-box">
            <div className="prefilled-box-header">
              {/* Not a <button>: the locked questionnaire disables its fieldset, which
                  disables every button inside, but the box should still collapse. */}
              <span
                role="button"
                tabIndex={0}
                className="prefilled-box-toggle"
                aria-expanded={authRepBoxOpen}
                onClick={() => setAuthRepBoxOpen(open => !open)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAuthRepBoxOpen(open => !open); } }}
              >
                <span className="toggle-icon">{authRepBoxOpen ? '▼' : '►'}</span>
                AUTHORIZED REPRESENTATIVE (Signing Official)
              </span>
            </div>
            {authRepBoxOpen && (
              <div className="prefilled-box-body">
                <p className="section-note">
                  Same signing official as above, pre-filled from your Company Information page.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name:</label>
                    <input type="text" value={applicantValues.signingOfficialFirstName || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                  <div className="form-group">
                    <label>Last Name:</label>
                    <input type="text" value={applicantValues.signingOfficialLastName || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Street:</label>
                  <input type="text" value={applicantValues.businessAddressLine1 || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>City:</label>
                    <input type="text" value={applicantValues.businessAddressCity || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                  <div className="form-group">
                    <label>Country:</label>
                    <input type="text" value="UNITED STATES" readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                  <div className="form-group">
                    <label>Zip/Postal Code:</label>
                    <input type="text" value={applicantValues.businessAddressZip || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number:</label>
                    <input type="text" value={applicantValues.signingOfficialPhone || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                  <div className="form-group">
                    <label>Email:</label>
                    <input type="text" value={applicantValues.signingOfficialEmail || ''} readOnly style={{ backgroundColor: '#f0f0f0' }} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ===== 3. Cover Page Supplement ===== */}
        <section className="form-section" id="cover-page-supplement">
          <h3>3. Cover Page Supplement</h3>
          <p className="section-note">
            The questions below make up the Cover Page Supplement.
          </p>


        <div className="form-subsection" id="vertebrate-animals">
          <h3>3.1 Vertebrate Animals Section</h3>
          
          <div className="form-group radio-group">
            <label className="required-indicator">Does the proposed project involve Vertebrate Animals?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="vertebrateAnimalsInvolved" 
                  value="Yes" 
                  checked={formData.vertebrateAnimalsInvolved === "Yes"} 
                  onChange={handleVertebrateAnimalsChange} 
                  required 
                />
                <span>Yes</span>
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="vertebrateAnimalsInvolved" 
                  value="No" 
                  checked={formData.vertebrateAnimalsInvolved === "No"} 
                  onChange={handleVertebrateAnimalsChange} 
                />
                <span>No</span>
              </label>
            </div>
          </div>

          {formData.vertebrateAnimalsInvolved === "Yes" && (
            <>
              <div className="form-group radio-group nested">
                <label className="required-indicator">If "Yes" are vertebrate animals euthanized?</label>
                <div className="radio-options">
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="animalsEuthanized" 
                      value="Yes" 
                      checked={formData.animalsEuthanized === "Yes"} 
                      onChange={handleInputChange} 
                      required 
                    />
                    <span>Yes</span>
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="animalsEuthanized" 
                      value="No" 
                      checked={formData.animalsEuthanized === "No"} 
                      onChange={handleInputChange} 
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>

              {formData.animalsEuthanized === "Yes" && (
                <>
                  <div className="form-group radio-group nested-deeper">
                    <label>Is method consistent with American Veterinary Medical Association (AVMA) guidelines?</label>
                    <div className="radio-options">
                      <label className="radio-label">
                        <input 
                          type="radio" 
                          name="avmaGuidelines" 
                          value="Yes" 
                          checked={formData.avmaGuidelines === "Yes"} 
                          onChange={handleInputChange} 
                          required 
                        />
                        <span>Yes</span>
                      </label>
                      <label className="radio-label">
                        <input 
                          type="radio" 
                          name="avmaGuidelines" 
                          value="No" 
                          checked={formData.avmaGuidelines === "No"} 
                          onChange={handleInputChange} 
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>

                  {formData.avmaGuidelines === "No" && (
                    <div className="form-group nested-deepest">
                      <label htmlFor="nonAvmaJustification">If "No" to AVMA guidelines, describe method and provide scientific justification</label>
                      <textarea 
                        id="nonAvmaJustification" 
                        name="nonAvmaJustification" 
                        value={formData.nonAvmaJustification} 
                        onChange={handleInputChange} 
                        rows="3"
                        required
                      ></textarea>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <div className="form-subsection" id="program-income">
          <h3>3.2 Program Income Section</h3>
          
          <div className="form-group radio-group">
            <label>Is program income anticipated during the periods for which the grant support is requested?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="programIncomeAnticipated" 
                  value="Yes" 
                  checked={formData.programIncomeAnticipated === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="programIncomeAnticipated" 
                  value="No" 
                  checked={formData.programIncomeAnticipated === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.programIncomeAnticipated === "Yes" && (
            <div className="form-group nested">
              <label>
                If you checked "Yes" above (indicating that program income is anticipated),
                give the anticipated amount and source(s) for each budget period:
              </label>

              <table className="program-income-table">
                <thead>
                  <tr>
                    <th>Budget Period</th>
                    <th>Anticipated Amount ($)</th>
                    <th>Source(s)</th>
                    <th aria-label="Remove row"></th>
                  </tr>
                </thead>
                <tbody>
                  {(formData.programIncomePeriods || []).length === 0 ? (
                    <tr>
                      <td colSpan={4} className="program-income-empty">
                        No budget periods added yet.
                      </td>
                    </tr>
                  ) : (
                    (formData.programIncomePeriods || []).map((row, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="text"
                            value={row.budgetPeriod || ''}
                            onChange={(e) => updateProgramIncomePeriod(index, 'budgetPeriod', e.target.value)}
                            placeholder="e.g., Year 1"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.anticipatedAmount || ''}
                            onChange={(e) => updateProgramIncomePeriod(index, 'anticipatedAmount', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.sources || ''}
                            onChange={(e) => updateProgramIncomePeriod(index, 'sources', e.target.value)}
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-site-btn"
                            onClick={() => removeProgramIncomePeriod(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>

              <button type="button" className="add-site-btn" onClick={addProgramIncomePeriod}>
                Add Income Budget Period
              </button>

              <label htmlFor="programIncomeDetails">Additional notes (optional):</label>
              <textarea
                id="programIncomeDetails"
                name="programIncomeDetails"
                value={formData.programIncomeDetails}
                onChange={handleInputChange}
                rows="2"
              ></textarea>
            </div>
          )}
        </div>

        <div className="form-subsection" id="human-embryonic-stem-cells">
          <h3>3.3 Human Embryonic Stem Cells Section</h3>
          
          <div className="form-group radio-group">
            <label>Does the proposed project involve human embryonic stem cells?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="humanEmbryonicStemCells" 
                  value="Yes" 
                  checked={formData.humanEmbryonicStemCells === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="humanEmbryonicStemCells" 
                  value="No" 
                  checked={formData.humanEmbryonicStemCells === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.humanEmbryonicStemCells === "Yes" && (
            <div className="form-group nested">
              <p>
                If the proposed project involves human embryonic stem cells, list below the registration number of the specific cell line(s) from the following list: 
                <a href="https://grants.nih.gov/stem_cells/registry/current.htm" target="_blank" rel="noopener noreferrer">
                  https://grants.nih.gov/stem_cells/registry/current.htm
                </a>
                . Or, if a specific stem cell line cannot be referenced at this time, check the box indicating that one from the registry will be used.
              </p>
              
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    name="registryCellLineToBeUsed" 
                    checked={formData.registryCellLineToBeUsed} 
                    onChange={handleCheckboxChange} 
                  />
                  Specific stem cell line cannot be referenced at this time. One from the registry will be used.
                </label>
              </div>
              
              {!formData.registryCellLineToBeUsed && (
                <div className="form-group">
                  <label htmlFor="cellLines">Cell Line(s): (Example: 0004)</label>
                  <input 
                    type="text" 
                    id="cellLines" 
                    name="cellLines" 
                    value={formData.cellLines} 
                    onChange={handleInputChange} 
                    required={formData.humanEmbryonicStemCells === "Yes" && !formData.registryCellLineToBeUsed}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-subsection" id="human-fetal-tissue">
          <h3>3.4 Human Fetal Tissue Section</h3>
          
          <div className="form-group radio-group">
            <label>Does the proposed project involve human fetal tissue obtained from elective abortions?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="humanFetalTissue" 
                  value="Yes" 
                  checked={formData.humanFetalTissue === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="humanFetalTissue" 
                  value="No" 
                  checked={formData.humanFetalTissue === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>


          {formData.humanFetalTissue === "Yes" && (
            <p className="field-help-text">
              If yes, then please upload the HFT Compliance Assurance and HFT Sample IRB Consent
              Forms on the Document Upload page.
            </p>
          )}
        </div>

        <div className="form-subsection" id="inventions-patents">
          <h3>3.5 Inventions and Patents Section</h3>
          <p className="section-note">(for Renewal applications)</p>
          
          <div className="form-group radio-group">
            <label>Inventions and Patents</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="hasInventionsAndPatents" 
                  value="Yes" 
                  checked={formData.hasInventionsAndPatents === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="hasInventionsAndPatents" 
                  value="No" 
                  checked={formData.hasInventionsAndPatents === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.hasInventionsAndPatents === "Yes" && (
            <div className="form-group radio-group nested">
              <label>Previously Reported</label>
              <div className="radio-options">
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="previouslyReported" 
                    value="Yes" 
                    checked={formData.previouslyReported === "Yes"} 
                    onChange={handleRadioChange} 
                    required 
                  />
                  Yes
                </label>
                <label className="radio-label">
                  <input 
                    type="radio" 
                    name="previouslyReported" 
                    value="No" 
                    checked={formData.previouslyReported === "No"} 
                    onChange={handleRadioChange} 
                  />
                  No
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="form-subsection" id="change-investigator">
          <h3>3.6 Change of Investigator/Change of Recipient Organization</h3>
          
          <div className="form-group radio-group">
            <label>Was there a change in the investigator or recipient organization?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="changeInInvestigatorOrOrg" 
                  value="Yes" 
                  checked={formData.changeInInvestigatorOrOrg === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="changeInInvestigatorOrOrg" 
                  value="No" 
                  checked={formData.changeInInvestigatorOrOrg === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.changeInInvestigatorOrOrg === "Yes" && (
            <div className="nested">
              <div className="form-group">
                <h4 className="subsection-title">Change of Project Director/Principal Investigator</h4>
                <p className="subsection-label">Name of former Project Director/Principal Investigator</p>
                
                <div className="pi-name-fields">
                  <div className="form-group small">
                    <label htmlFor="formerPIPrefix">Prefix</label>
                    <input 
                      type="text" 
                      id="formerPIPrefix" 
                      name="formerPIPrefix" 
                      value={formData.formerPIPrefix} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="formerPIFirstName">* First Name</label>
                    <input 
                      type="text" 
                      id="formerPIFirstName" 
                      name="formerPIFirstName" 
                      value={formData.formerPIFirstName} 
                      onChange={handleInputChange}
                      required={formData.changeInInvestigatorOrOrg === "Yes"}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="formerPIMiddleName">Middle Name</label>
                    <input 
                      type="text" 
                      id="formerPIMiddleName" 
                      name="formerPIMiddleName" 
                      value={formData.formerPIMiddleName} 
                      onChange={handleInputChange} 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="formerPILastName">* Last Name</label>
                    <input 
                      type="text" 
                      id="formerPILastName" 
                      name="formerPILastName" 
                      value={formData.formerPILastName} 
                      onChange={handleInputChange}
                      required={formData.changeInInvestigatorOrOrg === "Yes"} 
                    />
                  </div>
                  
                  <div className="form-group small">
                    <label htmlFor="formerPISuffix">Suffix</label>
                    <input 
                      type="text" 
                      id="formerPISuffix" 
                      name="formerPISuffix" 
                      value={formData.formerPISuffix} 
                      onChange={handleInputChange} 
                    />
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <h4 className="subsection-title">Change of Recipient Organization</h4>
                <label htmlFor="formerOrganizationName">* Name of former organization</label>
                <input 
                  type="text" 
                  id="formerOrganizationName" 
                  name="formerOrganizationName" 
                  value={formData.formerOrganizationName} 
                  onChange={handleInputChange} 
                />
              </div>
            </div>
          )}
        </div>
        </section>

        <section className="form-section" id="additional-questions">
          <h3>4. Other Project Information</h3>
          
          <div className="form-group radio-group">
            <label>1. * Are Human Subjects Involved</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="humanSubjectsInvolved" 
                  value="Yes" 
                  checked={formData.humanSubjectsInvolved === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="humanSubjectsInvolved" 
                  value="No" 
                  checked={formData.humanSubjectsInvolved === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.humanSubjectsInvolved === "Yes" && (
            <div className="nested">
              <div className="form-group radio-group">
                <label>1.a Is the project exempt from Federal regulations?</label>
                <div className="radio-options">
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="isExemptFromFederalRegulations" 
                      value="Yes" 
                      checked={formData.isExemptFromFederalRegulations === "Yes"} 
                      onChange={handleRadioChange} 
                      required 
                    />
                    Yes
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="isExemptFromFederalRegulations" 
                      value="No" 
                      checked={formData.isExemptFromFederalRegulations === "No"} 
                      onChange={handleRadioChange} 
                    />
                    No
                  </label>
                </div>
              </div>
              
              {formData.isExemptFromFederalRegulations === "Yes" && (
                <div className="form-group nested">
                  <label>If yes, check the appropriate exemption number.</label>
                  <div className="exemption-checkboxes">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                      <label key={num} className="checkbox-label">
                        <input 
                          type="radio" 
                          name="exemptionNumber" 
                          value={num.toString()} 
                          checked={formData.exemptionNumber === num.toString()} 
                          onChange={handleRadioChange} 
                          required={formData.isExemptFromFederalRegulations === "Yes"}
                        />
                        {num}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {formData.isExemptFromFederalRegulations === "No" && (
                <>
                  <div className="form-group radio-group nested">
                    <label>If no, is the IRB review Pending?</label>
                    <div className="radio-options">
                      <label className="radio-label">
                        <input 
                          type="radio" 
                          name="isIRBPending" 
                          value="Yes" 
                          checked={formData.isIRBPending === "Yes"} 
                          onChange={handleRadioChange} 
                          required 
                        />
                        Yes
                      </label>
                      <label className="radio-label">
                        <input 
                          type="radio" 
                          name="isIRBPending" 
                          value="No" 
                          checked={formData.isIRBPending === "No"} 
                          onChange={handleRadioChange} 
                        />
                        No
                      </label>
                    </div>
                  </div>
                  
                  {formData.isIRBPending === "No" && (
                    <div className="form-group nested">
                      <label htmlFor="irbApprovalDate">IRB Approval Date</label>
                      <input 
                        type="date" 
                        id="irbApprovalDate" 
                        name="irbApprovalDate" 
                        value={formData.irbApprovalDate} 
                        onChange={handleInputChange} 
                        required={formData.isIRBPending === "No"} 
                      />
                    </div>
                  )}
                  
                  <div className="form-group nested">
                    <label htmlFor="humanSubjectsAssuranceNumber">Human Subjects Assurance Number</label>
                    <input 
                      type="text" 
                      id="humanSubjectsAssuranceNumber" 
                      name="humanSubjectsAssuranceNumber" 
                      value={formData.humanSubjectsAssuranceNumber} 
                      onChange={handleInputChange} 
                      required={formData.isExemptFromFederalRegulations === "No"} 
                    />
                  </div>
                </>
              )}
              
              <div className="form-group nested">
                <label htmlFor="humanSpecimensExplanation">
                  If yes, provide an explanation for any use of human specimens and/or data not considered to be human subjects research.
                </label>
                <textarea 
                  id="humanSpecimensExplanation" 
                  name="humanSpecimensExplanation" 
                  value={formData.humanSpecimensExplanation} 
                  onChange={handleInputChange} 
                  rows="3"
                />
              </div>
            </div>
          )}
          
          <div className="form-group radio-group">
            <label className="required-indicator">2. Are Vertebrate Animals Used?</label>
            {/* Answered once, in the Cover Page Supplement; shown here read-only so
                the two can never disagree. */}
            <div className="radio-options">
              {['Yes', 'No'].map(option => (
                <label className="radio-label" key={option}>
                  <input
                    type="radio"
                    name="vertebrateAnimalsUsedMirror"
                    value={option}
                    checked={(formData.vertebrateAnimalsInvolved || 'No') === option}
                    disabled
                    readOnly
                  />
                  {option}
                </label>
              ))}
            </div>
            <p className="section-note">
              Pulled from "Does the proposed project involve Vertebrate Animals?" in section 3.1.
              Change it there.
            </p>
          </div>
          
          {formData.vertebrateAnimalsInvolved === "Yes" && (
            <div className="nested">
              <div className="form-group radio-group">
                <label className="required-indicator">2.a If YES to Vertebrate Animals, is the IACUC review Pending?</label>
                <div className="radio-options">
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="iacucReviewPending" 
                      value="Yes" 
                      checked={formData.iacucReviewPending === "Yes"} 
                      onChange={handleInputChange} 
                      required 
                    />
                    <span>Yes</span>
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="iacucReviewPending" 
                      value="No" 
                      checked={formData.iacucReviewPending === "No"} 
                      onChange={handleInputChange} 
                    />
                    <span>No</span>
                  </label>
                </div>
              </div>
              
              {formData.iacucReviewPending === "No" && (
                <div className="form-group nested">
                  <label htmlFor="iacucApprovalDate">IACUC Approval Date</label>
                  <input 
                    type="date" 
                    id="iacucApprovalDate" 
                    name="iacucApprovalDate" 
                    value={formData.iacucApprovalDate} 
                    onChange={handleInputChange} 
                    required={formData.iacucReviewPending === "No"} 
                  />
                </div>
              )}
              
              <div className="form-group nested">
                <label htmlFor="assuranceNumber">Assurance Number</label>
                <input 
                  type="text" 
                  id="assuranceNumber" 
                  name="assuranceNumber" 
                  value={formData.assuranceNumber} 
                  onChange={handleInputChange} 
                  required={formData.vertebrateAnimalsInvolved === "Yes" || formData.vertebrateAnimalsUsed === "Yes"} 
                />
              </div>
            </div>
          )}

          <div className="form-group radio-group">
            <label>3. * Is proprietary/privileged information included in the application?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="containsProprietaryInfo" 
                  value="Yes" 
                  checked={formData.containsProprietaryInfo === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="containsProprietaryInfo" 
                  value="No" 
                  checked={formData.containsProprietaryInfo === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group radio-group">
            <label>4.a. * Does this project have an actual or potential impact - positive or negative - on the environment?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="hasEnvironmentalImpact" 
                  value="Yes" 
                  checked={formData.hasEnvironmentalImpact === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="hasEnvironmentalImpact" 
                  value="No" 
                  checked={formData.hasEnvironmentalImpact === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>
          
          {formData.hasEnvironmentalImpact === "Yes" && (
            <>
              <div className="form-group nested">
                <label htmlFor="environmentalImpactExplanation">4.b. If yes, please explain:</label>
                <textarea 
                  id="environmentalImpactExplanation" 
                  name="environmentalImpactExplanation" 
                  value={formData.environmentalImpactExplanation} 
                  onChange={handleInputChange} 
                  rows="3"
                  required={formData.hasEnvironmentalImpact === "Yes"} 
                />
              </div>
              
              <div className="form-group radio-group nested">
                <label>4.c. If this project has an actual or potential impact on the environment, has an exemption been authorized or an environmental assessment (EA) or environmental impact statement (EIS) been performed?</label>
                <div className="radio-options">
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="environmentalAssessmentPerformed" 
                      value="Yes" 
                      checked={formData.environmentalAssessmentPerformed === "Yes"} 
                      onChange={handleRadioChange} 
                      required={formData.hasEnvironmentalImpact === "Yes"} 
                    />
                    Yes
                  </label>
                  <label className="radio-label">
                    <input 
                      type="radio" 
                      name="environmentalAssessmentPerformed" 
                      value="No" 
                      checked={formData.environmentalAssessmentPerformed === "No"} 
                      onChange={handleRadioChange} 
                    />
                    No
                  </label>
                </div>
              </div>
              
              {formData.environmentalAssessmentPerformed === "Yes" && (
                <div className="form-group nested-deeper">
                  <label htmlFor="environmentalAssessmentExplanation">4.d. If yes, please explain:</label>
                  <textarea 
                    id="environmentalAssessmentExplanation" 
                    name="environmentalAssessmentExplanation" 
                    value={formData.environmentalAssessmentExplanation} 
                    onChange={handleInputChange} 
                    rows="3"
                    required={formData.environmentalAssessmentPerformed === "Yes"} 
                  />
                </div>
              )}
            </>
          )}

          <div className="form-group radio-group">
            <label>5. * Is the research performance site designated, or eligible to be designated, as a historic place?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="isHistoricPlace" 
                  value="Yes" 
                  checked={formData.isHistoricPlace === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="isHistoricPlace" 
                  value="No" 
                  checked={formData.isHistoricPlace === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>
          
          {formData.isHistoricPlace === "Yes" && (
            <div className="form-group nested">
              <label htmlFor="historicPlaceExplanation">5.a. If yes, please explain:</label>
              <textarea 
                id="historicPlaceExplanation" 
                name="historicPlaceExplanation" 
                value={formData.historicPlaceExplanation} 
                onChange={handleInputChange} 
                rows="3"
                required={formData.isHistoricPlace === "Yes"} 
              />
            </div>
          )}

          <div className="form-group radio-group">
            <label>6. * Does this project involve activities outside of the United States or partnerships with international collaborators?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="involvesInternationalActivities" 
                  value="Yes" 
                  checked={formData.involvesInternationalActivities === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="involvesInternationalActivities" 
                  value="No" 
                  checked={formData.involvesInternationalActivities === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>
          
          {formData.involvesInternationalActivities === "Yes" && (
            <>
              <div className="form-group nested">
                <label htmlFor="internationalCountries">6.a. If yes, identify countries:</label>
                <textarea 
                  id="internationalCountries" 
                  name="internationalCountries" 
                  value={formData.internationalCountries} 
                  onChange={handleInputChange} 
                  rows="2"
                  required={formData.involvesInternationalActivities === "Yes"} 
                />
              </div>
              
              <div className="form-group nested">
                <label htmlFor="internationalExplanation">6.b. Optional Explanation:</label>
                <textarea 
                  id="internationalExplanation" 
                  name="internationalExplanation" 
                  value={formData.internationalExplanation} 
                  onChange={handleInputChange} 
                  rows="3"
                />
              </div>
            </>
          )}
        </section>

        <section className="form-section" id="performance-site">
          <h3>5. Project Performance Sites</h3>
          <p className="section-note">
            Every senior/key personnel must be associated with one of the organizations added here.
          </p>
          
          <div className="form-group">
            <label htmlFor="projectSites">
              {companyDisplayName ? `${companyDisplayName}'s` : "Company's"} Primary Performance Site and Additional Locations:
              <span className="sublabel">Where will the work be conducted? You must specify at least one site.</span>
            </label>
            
            <div className="project-sites-container">
              {formData.projectSites.map((site, index) => (
                <div key={index} className={`project-site ${site.isPrimary ? 'primary-site' : ''}`}>
                  {index > 0 && (
                    <div className="site-header">
                      <h4>Additional Performance Site #{index}</h4>
                      <div className="site-header-actions">
                        {(() => {
                          console.log('Additional site savedSites.length:', savedSites.length);
                          return savedSites.length > 0;
                        })() && (
                          <button 
                            type="button" 
                            className="autofill-btn" 
                            onClick={() => toggleSiteAutofill(index)}
                          >
                            Auto-fill Site
                          </button>
                        )}
                        <button
                          type="button"
                          className="remove-site-btn"
                          onClick={() => removeProjectSite(index)}
                          title="Remove this site"
                        >
                          Remove Site
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {index === 0 && (
                    <div className="site-header">
                      <h4>{companyDisplayName ? `${companyDisplayName}'s` : "Company's"} Primary Performance Site</h4>
                      {(() => {
                        console.log('savedSites.length:', savedSites.length, 'savedSites:', savedSites);
                        return savedSites.length > 0;
                      })() && (
                        <button 
                          type="button" 
                          className="autofill-btn" 
                          onClick={() => toggleSiteAutofill(0)}
                        >
                          Auto-fill Site
                        </button>
                      )}
                    </div>
                  )}

                  {showSiteAutofill[index] && (
                    <div className="autofill-dropdown">
                      <h5>Select a saved site:</h5>
                      {savedSites.map((site, siteIndex) => (
                        <button 
                          key={siteIndex} 
                          type="button" 
                          className="autofill-option" 
                          onClick={() => handleSiteAutofill(index, site)}
                        >
                          <div className="site-name">{site.organizationName}</div>
                          <div className="site-address">
                            {site.street1}, {site.city}, {site.state} {site.zipCode}
                          </div>
                        </button>
                      ))}
                      <button 
                        type="button" 
                        className="cancel-autofill" 
                        onClick={() => cancelSiteAutofill(index)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`site-${index}-org`}>Organization Name: <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="text"
                        id={`site-${index}-org`}
                        value={site.organizationName}
                        onChange={(e) => handleProjectSiteChange(index, 'organizationName', e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`site-${index}-uei`}>UEI Number: <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="text"
                        id={`site-${index}-uei`}
                        value={site.ueiNumber}
                        onChange={(e) => handleProjectSiteChange(index, 'ueiNumber', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`site-${index}-street1`}>Street 1: <span style={{ color: '#ef4444' }}>*</span></label>
                    <input
                      type="text"
                      id={`site-${index}-street1`}
                      value={site.street1}
                      onChange={(e) => handleProjectSiteChange(index, 'street1', e.target.value)}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor={`site-${index}-street2`}>Street 2 (optional):</label>
                    <input
                      type="text"
                      id={`site-${index}-street2`}
                      value={site.street2}
                      onChange={(e) => handleProjectSiteChange(index, 'street2', e.target.value)}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor={`site-${index}-city`}>City: <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="text"
                        id={`site-${index}-city`}
                        value={site.city}
                        onChange={(e) => handleProjectSiteChange(index, 'city', e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`site-${index}-state`}>State: <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="text"
                        id={`site-${index}-state`}
                        value={site.state}
                        onChange={(e) => handleProjectSiteChange(index, 'state', e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor={`site-${index}-zip`}>Zip/Postal Code: <span style={{ color: '#ef4444' }}>*</span></label>
                      <input
                        type="text"
                        id={`site-${index}-zip`}
                        value={site.zipCode}
                        onChange={(e) => handleProjectSiteChange(index, 'zipCode', e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor={`site-${index}-district`}>
                      Project/Performance Site Congressional District <span style={{ color: '#ef4444' }}>*</span> (<a href="https://www.house.gov/representatives/find-your-representative" target="_blank" rel="noopener noreferrer">
                        Click here to find
                      </a>):
                    </label>
                    <input
                      type="text"
                      id={`site-${index}-district`}
                      value={site.congressionalDistrict}
                      onChange={(e) => handleProjectSiteChange(index, 'congressionalDistrict', e.target.value)}
                      placeholder="PA-005"
                      pattern="[A-Z]{2}-\d{3}"
                      title="Format: XX-XXX (state-congressional district), e.g. PA-005"
                      required
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Format: <strong>XX-XXX</strong> (state-congressional district). Example: <strong>PA-005</strong>
                    </p>
                  </div>

                  {/* Subcontract question for additional sites only (not primary).
                      Placed after the site address/congressional district so the site is
                      fully identified before the sub-contract details are requested. */}
                  {index > 0 && (
                    <>
                      <div className="form-group" style={{ marginTop: '16px', padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                        <label style={{ fontWeight: '600', color: '#1e40af' }}>
                          Will this organization receive a sub-contract?
                        </label>
                        <p style={{ margin: '6px 0 0', fontSize: '13px', color: '#1e40af' }}>
                          Universities and Partnering Institutions/Companies will generally receive a sub-contract, if they include key personnel (e.g., Co-I).
                        </p>
                        <div className="radio-options" style={{ marginTop: '8px' }}>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name={`site-${index}-subcontract`}
                              value="Yes"
                              checked={site.willReceiveSubcontract === 'Yes'}
                              onChange={(e) => handleProjectSiteChange(index, 'willReceiveSubcontract', e.target.value)}
                            />
                            Yes
                          </label>
                          <label className="radio-label">
                            <input
                              type="radio"
                              name={`site-${index}-subcontract`}
                              value="No"
                              checked={site.willReceiveSubcontract === 'No'}
                              onChange={(e) => handleProjectSiteChange(index, 'willReceiveSubcontract', e.target.value)}
                            />
                            No
                          </label>
                        </div>

                        {/* Once the questionnaire is submitted the whole form is disabled,
                            which greys the radios out and makes the recorded answer hard to
                            read. State it in plain text so it stays legible. */}
                        {questionnaireMeta.isComplete && (
                          <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#1e3a8a', fontWeight: 600 }}>
                            Answer: {site.willReceiveSubcontract
                              ? site.willReceiveSubcontract
                              : 'Not answered'}
                          </p>
                        )}
                      </div>

                      {/* Sub-contract PI details if subcontract is Yes */}
                      {site.willReceiveSubcontract === 'Yes' && questionnaireMeta.isComplete && (
                        <div className="form-group" style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                          <p style={{ margin: '0 0 4px', fontSize: '13px' }}>
                            <strong>Sub-Contract PI:</strong> {site.subcontractPIName || 'Not provided'}
                          </p>
                          <p style={{ margin: '0 0 4px', fontSize: '13px' }}>
                            <strong>PI Email:</strong> {site.subcontractPIEmail || site.subcontractContactEmail || 'Not provided'}
                          </p>
                          <p style={{ margin: 0, fontSize: '13px' }}>
                            <strong>Business Office Email:</strong> {site.subcontractBusinessOfficeEmail || 'Not provided'}
                          </p>
                        </div>
                      )}

                      {site.willReceiveSubcontract === 'Yes' && !questionnaireMeta.isComplete && (
                        <div className="form-group" style={{ marginTop: '12px' }}>
                          <div className="form-group">
                            <label htmlFor={`site-${index}-subcontract-pi-name`}>
                              Sub-Contract PI: <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              type="text"
                              id={`site-${index}-subcontract-pi-name`}
                              value={site.subcontractPIName || ''}
                              onChange={(e) => handleProjectSiteChange(index, 'subcontractPIName', e.target.value)}
                              placeholder="Full name of the sub-contract PI"
                              required
                            />
                          </div>

                          <p style={{ fontSize: '13px', color: '#1e40af', margin: '12px 0 8px' }}>
                            Click the <strong>Send Request</strong> button to send instructions to the email(s) below and a secure link to upload the required documents.
                          </p>

                          <div className="form-group">
                            <label htmlFor={`site-${index}-subcontract-pi-email`}>
                              Email of Sub-Contract PI (e.g., University faculty member): <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            <input
                              type="email"
                              id={`site-${index}-subcontract-pi-email`}
                              value={site.subcontractPIEmail || ''}
                              onChange={(e) => handleProjectSiteChange(index, 'subcontractPIEmail', e.target.value)}
                              placeholder="Enter the sub-contract PI's email"
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor={`site-${index}-subcontract-bo-email`}>
                              Email of Sub-Contract PI's Business Office (if known):
                            </label>
                            <input
                              type="email"
                              id={`site-${index}-subcontract-bo-email`}
                              value={site.subcontractBusinessOfficeEmail || ''}
                              onChange={(e) => handleProjectSiteChange(index, 'subcontractBusinessOfficeEmail', e.target.value)}
                              placeholder="Business office email (will be CC'd)"
                            />
                          </div>

                          {(() => {
                            const canSend = !!site.subcontractPIEmail && !!site.subcontractPIName && !!site.organizationName && !sendingSubcontractEmails;
                            return (
                              <button
                                type="button"
                                onClick={() => handleSubcontractEmailPreview(index)}
                                disabled={!canSend}
                                style={{
                                  padding: '8px 16px',
                                  backgroundColor: sentSubcontractRequests.has(index) ? '#10b981' : '#3b82f6',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: canSend ? 'pointer' : 'not-allowed',
                                  opacity: canSend ? 1 : 0.6,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {sentSubcontractRequests.has(index) ? '✓ Request Sent' : 'Send Request'}
                              </button>
                            );
                          })()}
                          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                            {sentSubcontractRequests.has(index)
                              ? "Document request has been sent to the sub-contract PI, with the business office CC'd."
                              : "The request is emailed to the sub-contract PI, with the business office CC'd if provided."}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                </div>
              ))}
              
              {/* Save button shown when company info has no performance site yet */}
              {companyInfoAutofill && !companyInfoAutofill.performanceSiteAddressLine1 && !companyInfoAutofill.performanceSiteAddress && (
                <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '6px' }}>
                  <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#92400e' }}>
                    Your Company Info page doesn't have a performance site address saved yet. Click below to save the primary site here as the company-wide performance site.
                  </p>
                  <button
                    type="button"
                    onClick={handleSavePrimarySiteToCompanyInfo}
                    style={{
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Save Performance Site to Company Info
                  </button>
                </div>
              )}

              <button
                type="button"
                className="add-site-btn"
                onClick={addProjectSite}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
              >
                + Add Additional Performance Site
              </button>
            </div>
          </div>
        </section>

        {/* ===== 6. Senior/Key Personnel ===== */}
        <section className="form-section" id="key-personnel">
          <h3>6. Senior/Key Personnel</h3>

          <div className="section-callout">
            <p><strong>SBIR:</strong> The PI must be primarily employed (more than 50% time) by the applicant small business.</p>
            <p><strong>STTR:</strong> The PI may be employed by the applicant small business or the partnering non-profit research institution.</p>
            <p>If there is more than one PI, a Multiple PI Leadership Plan must be provided (upload it in the "Upload Documents" folder).</p>
          </div>

          <p className="section-note">
            Key personnel at another organization must sit at one of the performance sites in
            section 5. A biosketch is requested on the Upload Documents page for each person at{' '}
            {companyDisplayName || 'your company'}, and on the Sub-Contract Documents Management page
            for each person at another organization. Name, project role and position/title are shared
            with the biosketch, so editing them on either page updates both.
          </p>

          {(formData.keyPersonnel || []).map((person, index) => {
            const isLead = index === 0;
            const isClientCompany = isLead || (!!person.organizationName && person.organizationName === companyDisplayName);
            const readOnlyStyle = { backgroundColor: '#f0f0f0' };
            return (
              <div className="project-site" key={index}>
                <div className="site-header">
                  <h4>
                    {isLead
                      ? `Lead Investigator at ${companyDisplayName || 'your company'}`
                      : `Senior/Key Person #${index + 1}`}
                  </h4>
                  {index > 0 && (
                    <button
                      type="button"
                      className="remove-site-btn"
                      onClick={() => removeKeyPerson(index)}
                    >
                      Remove Person
                    </button>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`kp-${index}-first`}>First Name:</label>
                    <input
                      type="text"
                      id={`kp-${index}-first`}
                      value={person.firstName || ''}
                      onChange={(e) => handleKeyPersonChange(index, 'firstName', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`kp-${index}-last`}>Last Name:</label>
                    <input
                      type="text"
                      id={`kp-${index}-last`}
                      value={person.lastName || ''}
                      onChange={(e) => handleKeyPersonChange(index, 'lastName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor={`kp-${index}-era`}>eRA Commons ID/Username:</label>
                    <input
                      type="text"
                      id={`kp-${index}-era`}
                      value={person.eraCommonsUsername || ''}
                      onChange={(e) => handleKeyPersonChange(index, 'eraCommonsUsername', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor={`kp-${index}-role`}>Project Role:</label>
                    <select
                      id={`kp-${index}-role`}
                      value={person.projectRole || ''}
                      onChange={(e) => handleKeyPersonChange(index, 'projectRole', e.target.value)}
                    >
                      <option value="">-- Select --</option>
                      {PROJECT_ROLES.map(role => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {person.projectRole === 'Other (Specify)' && (
                  <div className="form-group">
                    <label htmlFor={`kp-${index}-role-other`}>Specify project role:</label>
                    <input
                      type="text"
                      id={`kp-${index}-role-other`}
                      value={person.projectRoleOther || ''}
                      onChange={(e) => handleKeyPersonChange(index, 'projectRoleOther', e.target.value)}
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor={`kp-${index}-position`}>Position/Title:</label>
                  <input
                    type="text"
                    id={`kp-${index}-position`}
                    value={person.positionTitle || ''}
                    onChange={(e) => handleKeyPersonChange(index, 'positionTitle', e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`kp-${index}-org`}>Organization Name:</label>
                  {isLead ? (
                    <input
                      type="text"
                      id={`kp-${index}-org`}
                      value={companyDisplayName}
                      readOnly
                      style={readOnlyStyle}
                    />
                  ) : (
                    <select
                      id={`kp-${index}-org`}
                      value={person.organizationName || ''}
                      onChange={(e) => handleKeyPersonChange(index, 'organizationName', e.target.value)}
                    >
                      <option value="">-- Select an organization --</option>
                      {keyPersonOrganizations.map(org => (
                        <option key={org} value={org}>{org}</option>
                      ))}
                    </select>
                  )}
                </div>

                {isClientCompany ? (
                  <>
                    <p className="section-note">Business address, from your Company Information page.</p>
                    <div className="form-group">
                      <label htmlFor={`kp-${index}-street1`}>Street:</label>
                      <input type="text" id={`kp-${index}-street1`} value={person.street1 || ''} readOnly style={readOnlyStyle} />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label htmlFor={`kp-${index}-city`}>City:</label>
                        <input type="text" id={`kp-${index}-city`} value={person.city || ''} readOnly style={readOnlyStyle} />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`kp-${index}-country`}>Country:</label>
                        <input type="text" id={`kp-${index}-country`} value="UNITED STATES" readOnly style={readOnlyStyle} />
                      </div>
                      <div className="form-group">
                        <label htmlFor={`kp-${index}-zip`}>Zip/Postal Code:</label>
                        <input type="text" id={`kp-${index}-zip`} value={person.zipCode || ''} readOnly style={readOnlyStyle} />
                      </div>
                    </div>
                  </>
                ) : (
                  person.organizationName && (
                    <p className="section-note">
                      Business address and contact information completed on the Sub-Contract
                      Documents Management Page.
                    </p>
                  )
                )}
              </div>
            );
          })}

          <button type="button" className="add-site-btn" onClick={addKeyPerson}>
            + Add Additional Senior/Key Personnel
          </button>
        </section>

        {/* ===== 7. SBIR/STTR Information ===== */}
        <section className="form-section" id="sbir-sttr-program">
          <h3>7. SBIR/STTR Information</h3>
          <h4 className="subsection-title">7.1 SBIR/STTR Program Information</h4>
          
          <div className="form-group">
            <label htmlFor="agency" className="required-field">Agency to which you are applying (select only one)</label>
            <div className="radio-options-vertical">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="agency" 
                  value="DOE" 
                  checked={formData.agency === "DOE"} 
                  onChange={handleRadioChange} 
                  required 
                />
                DOE
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="agency" 
                  value="HHS" 
                  checked={formData.agency === "HHS"} 
                  onChange={handleRadioChange} 
                />
                HHS
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="agency" 
                  value="USDA" 
                  checked={formData.agency === "USDA"} 
                  onChange={handleRadioChange} 
                />
                USDA
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="agency" 
                  value="Other" 
                  checked={formData.agency === "Other"} 
                  onChange={handleRadioChange} 
                />
                Other:
                {formData.agency === "Other" && (
                  <input 
                    type="text" 
                    name="agencyOther" 
                    value={formData.agencyOther} 
                    onChange={handleInputChange}
                    className="inline-input"
                    required
                  />
                )}
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="programType" className="required-field">Program Type (select only one)</label>
            <div className="radio-options-vertical">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="programType" 
                  value="SBIR" 
                  checked={formData.programType === "SBIR"} 
                  onChange={handleRadioChange} 
                  required 
                />
                SBIR
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="programType" 
                  value="STTR" 
                  checked={formData.programType === "STTR"} 
                  onChange={handleRadioChange} 
                />
                STTR
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="programType" 
                  value="Both" 
                  checked={formData.programType === "Both"} 
                  onChange={handleRadioChange} 
                />
                Both (See agency-specific instructions to determine whether a particular agency allows a single submission for both SBIR and STTR)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="applicationType" className="required-field">Application Type (select only one)</label>
            <div className="radio-options-vertical">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Phase I" 
                  checked={formData.applicationType === "Phase I"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Phase I
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Phase II" 
                  checked={formData.applicationType === "Phase II"} 
                  onChange={handleRadioChange} 
                />
                Phase II
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Fast-Track" 
                  checked={formData.applicationType === "Fast-Track"} 
                  onChange={handleRadioChange} 
                />
                Fast-Track
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Direct Phase II" 
                  checked={formData.applicationType === "Direct Phase II"} 
                  onChange={handleRadioChange} 
                />
                Direct Phase II
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Phase IIA" 
                  checked={formData.applicationType === "Phase IIA"} 
                  onChange={handleRadioChange} 
                />
                Phase IIA
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Phase IIB" 
                  checked={formData.applicationType === "Phase IIB"} 
                  onChange={handleRadioChange} 
                />
                Phase IIB
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="applicationType" 
                  value="Commercialization Readiness Program" 
                  checked={formData.applicationType === "Commercialization Readiness Program"} 
                  onChange={handleRadioChange} 
                />
                Commercialization Readiness Program (See agency-specific instructions to determine application type participation.)
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="phaseILetterOfIntentNumber">Phase I Letter of Intent Number:</label>
            <input 
              type="text" 
              id="phaseILetterOfIntentNumber" 
              name="phaseILetterOfIntentNumber" 
              value={formData.phaseILetterOfIntentNumber} 
              onChange={handleInputChange} 
            />
          </div>

          <div className="form-group">
            <label htmlFor="agencyTopicSubtopic">Agency Topic/Subtopic:</label>
            <input 
              type="text" 
              id="agencyTopicSubtopic" 
              name="agencyTopicSubtopic" 
              value={formData.agencyTopicSubtopic} 
              onChange={handleInputChange} 
            />
          </div>


        <div className="form-subsection" id="eligibility-questions">
          <h3>7.2 SBIR/STTR Eligibility Questions</h3>
          <p className="section-description">Questions 1-8 must be completed by all SBIR and STTR Applicants:</p>
          
          <div className="form-group radio-group">
            <label className="required-field">1a. Do you certify that at the time of award your organization will meet the eligibility criteria for a small business as defined in the funding opportunity announcement?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="meetEligibilityCriteria" 
                  value="Yes" 
                  checked={formData.meetEligibilityCriteria === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="meetEligibilityCriteria" 
                  value="No" 
                  checked={formData.meetEligibilityCriteria === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="anticipatedPersonnelCount" className="required-field">1b. Anticipated Number of personnel to be employed at your organization at the time of award.</label>
            <input 
              type="number" 
              id="anticipatedPersonnelCount" 
              name="anticipatedPersonnelCount" 
              value={formData.anticipatedPersonnelCount} 
              onChange={handleInputChange} 
              min="1"
              required 
            />
          </div>

          <div className="form-group radio-group">
            <label>1c. Is your small business majority owned by venture capital operating companies, hedge funds, or private equity firms?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="majorityOwnedByVC" 
                  value="Yes" 
                  checked={formData.majorityOwnedByVC === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="majorityOwnedByVC" 
                  value="No" 
                  checked={formData.majorityOwnedByVC === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group radio-group">
            <label>1d. Is your small business a Faculty or Student-Owned entity?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="facultyOrStudentOwned" 
                  value="Yes" 
                  checked={formData.facultyOrStudentOwned === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="facultyOrStudentOwned" 
                  value="No" 
                  checked={formData.facultyOrStudentOwned === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group radio-group">
            <label className="required-field">2. Does this application include subcontracts with Federal laboratories or any other Federal Government agencies?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="includesSubcontractsWithFedLabs" 
                  value="Yes" 
                  checked={formData.includesSubcontractsWithFedLabs === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="includesSubcontractsWithFedLabs" 
                  value="No" 
                  checked={formData.includesSubcontractsWithFedLabs === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.includesSubcontractsWithFedLabs === "Yes" && (
            <div className="form-group nested">
              <label htmlFor="fedLabsAgencies">If yes, insert names of the Federal Laboratories/agencies:</label>
              <textarea 
                id="fedLabsAgencies" 
                name="fedLabsAgencies" 
                value={formData.fedLabsAgencies} 
                onChange={handleInputChange} 
                rows="2"
                required={formData.includesSubcontractsWithFedLabs === "Yes"}
              />
            </div>
          )}

          <div className="form-group radio-group">
            <label className="required-field">3. Are you located in a HUBZone?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="locatedInHUBZone" 
                  value="Yes" 
                  checked={formData.locatedInHUBZone === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="locatedInHUBZone" 
                  value="No" 
                  checked={formData.locatedInHUBZone === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
            <p className="field-help-text">
              To find out if your business is in a HUBZone, use the mapping utility provided by the Small Business Administration at its <a href="https://maps.certify.sba.gov/hubzone/map#center=39.828200,-98.579500&zoom=5" target="_blank" rel="noopener noreferrer">website</a>.
            </p>
          </div>

          <div className="form-group radio-group">
            <label className="required-field">4. Will all research and development on the project be performed in its entirety in the United States?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="researchInUS" 
                  value="Yes" 
                  checked={formData.researchInUS === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="researchInUS" 
                  value="No" 
                  checked={formData.researchInUS === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.researchInUS === "No" && (
            <div className="form-group nested">
              <label htmlFor="researchNotInUSExplanation" className="required-field">Explanation:</label>
              <textarea 
                id="researchNotInUSExplanation" 
                name="researchNotInUSExplanation" 
                value={formData.researchNotInUSExplanation} 
                onChange={handleInputChange} 
                rows="3"
                required={formData.researchInUS === "No"}
              />
            </div>
          )}

          <div className="form-group radio-group">
            <label className="required-field">5. Has the applicant and/or Program Director/Principal Investigator submitted proposals for essentially equivalent work under other Federal program solicitations or received other Federal awards for essentially equivalent work?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="submittedEquivalentWork" 
                  value="Yes" 
                  checked={formData.submittedEquivalentWork === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="submittedEquivalentWork" 
                  value="No" 
                  checked={formData.submittedEquivalentWork === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          {formData.submittedEquivalentWork === "Yes" && (
            <div className="form-group nested">
              <label htmlFor="equivalentWorkAgencies" className="required-field">If yes, insert names of the Federal Laboratories/agencies:</label>
              <textarea 
                id="equivalentWorkAgencies" 
                name="equivalentWorkAgencies" 
                value={formData.equivalentWorkAgencies} 
                onChange={handleInputChange} 
                rows="2"
                required={formData.submittedEquivalentWork === "Yes"}
              />
            </div>
          )}

          <div className="form-group radio-group">
            <label className="required-field">6. Disclosure Permission Statement: If this application does not result in an award, is the Government permitted to disclose the title of your proposed project, and the name, address, telephone number and email address of the official signing for the applicant organization to state-level economic development organizations that may be interested in contacting you for further information (e.g., possible collaborations, investment)?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="allowInfoDisclosure" 
                  value="Yes" 
                  checked={formData.allowInfoDisclosure === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="allowInfoDisclosure" 
                  value="No" 
                  checked={formData.allowInfoDisclosure === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
          </div>

          <div className="form-group radio-group">
            <label className="required-field">7. Does the application include a request of SBIR or STTR funds for Technical and Business Assistance (TABA)?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="requestingTABAFunds" 
                  value="Yes" 
                  checked={formData.requestingTABAFunds === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="requestingTABAFunds" 
                  value="No" 
                  checked={formData.requestingTABAFunds === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
            <p className="field-help-text">
              (Please answer no if you plan to use the agency TABA vendor, which does not require you to include a request for TABA funds in your application.)
            </p>
          </div>

          {formData.requestingTABAFunds === "Yes" && (
            <div className="nested">
              <div className="form-group">
                <label htmlFor="tabaFundsBudget" className="required-field">TABA Funds Budget Request:</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">$</span>
                  <input 
                    type="number" 
                    id="tabaFundsBudget" 
                    name="tabaFundsBudget" 
                    value={formData.tabaFundsBudget} 
                    onChange={handleInputChange} 
                    min="0"
                    step="0.01"
                    required={formData.requestingTABAFunds === "Yes"}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="tabaFundsJustification" className="required-field">TABA Funds Justification:</label>
                <textarea 
                  id="tabaFundsJustification" 
                  name="tabaFundsJustification" 
                  value={formData.tabaFundsJustification} 
                  onChange={handleInputChange} 
                  rows="3"
                  required={formData.requestingTABAFunds === "Yes"}
                  placeholder="Please follow agency specific instructions to provide the budget request and justification."
                />
              </div>
            </div>
          )}
        </div>

        {/* Only show SBIR/STTR sections if program type is SBIR, STTR, or Both */}
        {(formData.programType === 'SBIR' || formData.programType === 'STTR' || formData.programType === 'Both') && (
          <div className="form-subsection">
            <div className="section-divider"></div>
              
            {formData.programType !== "STTR" && (
              <div>
                <div className="subsection-heading">
                  <h4>SBIR-Specific Questions</h4>
                  <p className="instructions">
                    Questions 9 and 10 apply only to SBIR applications. If you are submitting ONLY an STTR application, leave questions 9 and 10 blank and proceed to question 11.
                  </p>
                </div>

                <div className="form-group radio-group">
                  <label className="required-field">9. Have you received SBIR Phase II awards from the Federal Government?</label>
                  <div className="radio-options">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="receivedSBIRPhaseII" 
                        value="Yes" 
                        checked={formData.receivedSBIRPhaseII === "Yes"} 
                        onChange={handleRadioChange} 
                        disabled={formData.programType === "STTR"}
                      />
                      Yes
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="receivedSBIRPhaseII" 
                        value="No" 
                        checked={formData.receivedSBIRPhaseII === "No"} 
                        onChange={handleRadioChange}
                        disabled={formData.programType === "STTR"}
                      />
                      No
                    </label>
                  </div>
                  {formData.receivedSBIRPhaseII === "Yes" && (
                    <p className="field-help-text">
                      If yes, provide a company commercialization history in accordance with agency-specific
                      instructions. A "Commercialization History" upload is added to the Upload Documents
                      page for this grant application — upload the file there.
                    </p>
                  )}
                </div>

                <div className="form-group radio-group">
                  <label className="required-field">10. Will the Project Director/Principal Investigator have his/her primary employment with the small business at the time of award?</label>
                  <div className="radio-options">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="piPrimaryEmploymentWithSmallBusiness" 
                        value="Yes" 
                        checked={formData.piPrimaryEmploymentWithSmallBusiness === "Yes"} 
                        onChange={handleRadioChange} 
                        disabled={formData.programType === "STTR"}
                        required={formData.programType === "SBIR" || formData.programType === "Both"}
                      />
                      Yes
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="piPrimaryEmploymentWithSmallBusiness" 
                        value="No" 
                        checked={formData.piPrimaryEmploymentWithSmallBusiness === "No"} 
                        onChange={handleRadioChange}
                        disabled={formData.programType === "STTR"}
                        required={formData.programType === "SBIR" || formData.programType === "Both"}
                      />
                      No
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="section-divider"></div>

            {formData.programType !== "SBIR" && (
              <div>
                <div className="subsection-heading">
                  <h4>STTR-Specific Questions</h4>
                  <p className="instructions">
                    Questions 11 - 13 apply only to STTR applications. If you are submitting ONLY an SBIR application, leave questions 11 - 13 blank.
                  </p>
                </div>

                <div className="form-group radio-group">
                  <label className="required-field">11. Please indicate whether the answer to BOTH of the following questions is TRUE:</label>
                  <p className="sublabel">
                    (1) Does the Project Director/Principal Investigator have a formal appointment or commitment either with the small business directly (as an employee or a contractor) OR as an employee of the Research Institution, which in turn has made a commitment to the small business through the STTR application process; AND
                    <br />
                    (2) Will the Project Director/Principal Investigator devote at least 10% effort to the proposed project?
                  </p>
                  <div className="radio-options">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="piCommitmentAndEffort" 
                        value="Yes" 
                        checked={formData.piCommitmentAndEffort === "Yes"} 
                        onChange={handleRadioChange} 
                        disabled={formData.programType === "SBIR"}
                        required={formData.programType === "STTR" || formData.programType === "Both"}
                      />
                      Yes
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="piCommitmentAndEffort" 
                        value="No" 
                        checked={formData.piCommitmentAndEffort === "No"} 
                        onChange={handleRadioChange}
                        disabled={formData.programType === "SBIR"}
                        required={formData.programType === "STTR" || formData.programType === "Both"}
                      />
                      No
                    </label>
                  </div>
                </div>

                <div className="form-group radio-group">
                  <label className="required-field">12. In the joint research and development proposed in this project, does the small business perform at least 40% of the work and the research institution named in the application perform at least 30% of the work?</label>
                  <div className="radio-options">
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="workDistributionRequirementMet" 
                        value="Yes" 
                        checked={formData.workDistributionRequirementMet === "Yes"} 
                        onChange={handleRadioChange} 
                        disabled={formData.programType === "SBIR"}
                        required={formData.programType === "STTR" || formData.programType === "Both"}
                      />
                      Yes
                    </label>
                    <label className="radio-label">
                      <input 
                        type="radio" 
                        name="workDistributionRequirementMet" 
                        value="No" 
                        checked={formData.workDistributionRequirementMet === "No"} 
                        onChange={handleRadioChange}
                        disabled={formData.programType === "SBIR"}
                        required={formData.programType === "STTR" || formData.programType === "Both"}
                      />
                      No
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="nonprofitResearchPartnerUEI" className="required-field">13. Provide UEI of non-profit research partner for STTR.</label>
                  <input 
                    type="text" 
                    id="nonprofitResearchPartnerUEI" 
                    name="nonprofitResearchPartnerUEI" 
                    value={formData.nonprofitResearchPartnerUEI} 
                    onChange={handleInputChange} 
                    disabled={formData.programType === "SBIR"}
                    required={formData.programType === "STTR" || formData.programType === "Both"}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        </section>

        <section className="form-section" id="human-subjects-clinical-trials">
          <h3>8. Human Subjects and Clinical Trials</h3>

          <div className="form-group radio-group">
            <label className="required-field">Does any of the proposed research in the application involve human specimens and/or data?</label>
            <div className="radio-options">
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="involvesHumanSpecimensOrData" 
                  value="Yes" 
                  checked={formData.involvesHumanSpecimensOrData === "Yes"} 
                  onChange={handleRadioChange} 
                  required 
                />
                Yes
              </label>
              <label className="radio-label">
                <input 
                  type="radio" 
                  name="involvesHumanSpecimensOrData" 
                  value="No" 
                  checked={formData.involvesHumanSpecimensOrData === "No"} 
                  onChange={handleRadioChange} 
                />
                No
              </label>
            </div>
            {formData.involvesHumanSpecimensOrData === "Yes" && (
              <p className="field-help-text">
                If yes, we will follow up with additional questions.
              </p>
            )}
          </div>
        </section>

        </fieldset>

        {questionnaireMeta.isComplete && (
          <div style={{ marginTop: '20px', padding: '14px', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <div>
                <p style={{ margin: 0, fontWeight: 600, color: '#065f46' }}>Questionnaire marked complete</p>
                {questionnaireMeta.completedAt && (
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#047857' }}>
                    Completed on {new Date(questionnaireMeta.completedAt).toLocaleString()}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setShowUnlockModal(true)}
                style={{ padding: '8px 14px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 600, cursor: 'pointer' }}
              >
                Unlock & Modify
              </button>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="save-draft-btn"
            onClick={handleSaveDraft}
            disabled={isSaving || questionnaireMeta.isComplete}
            title={questionnaireMeta.isComplete ? 'Unlock the questionnaire to make changes' : ''}
          >
            {isSaving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            type="submit"
            className="save-btn mark-complete-btn"
            disabled={isSaving || questionnaireMeta.isComplete}
            title={questionnaireMeta.isComplete ? 'Already marked complete — unlock to re-submit' : ''}
          >
            {isSaving ? 'Saving...' : 'Mark as Complete'}
          </button>
        </div>
        
        {error && (
          <div className="save-error-message" style={{marginTop: '10px'}}>
            {error}
          </div>
        )}
      </form>
        </div>
      </div>

      {saveSuccess && (
        <div className="save-success-message">
          Questionnaire saved successfully!
        </div>
      )}

      {/* Changing an applicant answer also changes the Company Information page,
          so the client confirms before anything is written. */}
      {applicantConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Confirm this change?</h3>
              <p className="text-sm text-gray-600 mb-3">
                These answers will also be updated on your Company Information page.
              </p>
              <ul className="text-sm text-gray-700 list-disc pl-5">
                {applicantConfirm.map(change => (
                  <li key={change.field}>
                    <strong>{change.label}:</strong>{' '}
                    {String(change.from || '(blank)')} → {String(change.to || '(blank)')}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 px-6 py-3 flex justify-end gap-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setApplicantConfirm(null)}
                disabled={savingApplicant}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmApplicantSave}
                disabled={savingApplicant}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-60"
              >
                {savingApplicant ? 'Saving...' : 'Proceed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unlock Questionnaire confirmation modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14a2 2 0 001.74-3l-7.07-12a2 2 0 00-3.48 0l-7.07 12a2 2 0 001.74 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Unlock Questionnaire?</h3>
                  <p className="text-sm text-gray-600">
                    This questionnaire was previously marked complete. Unlocking it will let you edit it again and will send an email to the assigned StreamLaunch admins letting them know the questionnaire is being modified.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-3 flex justify-end gap-2 border-t border-gray-200">
              <button
                onClick={() => setShowUnlockModal(false)}
                disabled={unlocking}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={performUnlock}
                disabled={unlocking}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium disabled:opacity-60"
              >
                {unlocking ? 'Unlocking...' : 'Unlock & Notify Admins'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subcontract Email Preview/Edit Modal */}
      <EmailPreviewEditModal
        isOpen={showSubcontractEmailPreview}
        onClose={() => setShowSubcontractEmailPreview(false)}
        onSend={handleSendSubcontractEmails}
        templateKey="subcontract_document_request"
        variables={subcontractEmailVariables}
        recipientEmail={subcontractRecipientEmail}
        ccEmails={subcontractCcEmails}
        title="Subcontract Document Request Email"
        loading={sendingSubcontractEmails}
        showEditToggle={isAdmin}
        allowTemplateUpdate={isAdmin}
      />
    </div>
  );
};

export default GrantQuestionnaire;